import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  useMediaQuery,
  useTheme,
  ThemeProvider,
  createTheme,
  Stack,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Avatar,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Fade,
  Grow,
  Zoom,
  Collapse,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Backdrop,
  Modal,
  Fab,
  MobileStepper,
  Pagination,
  Skeleton,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  GlobalStyles,
} from "@mui/material";
import {
  Save as SaveIcon,
  Print as PrintIcon,
  PhotoCamera as PhotoIcon,
  Fingerprint as FingerprintIcon,
  Description as BondIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Diamond as DiamondIcon,
  Payment as PaymentIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  CameraAltRounded as CameraAltRoundedIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  QrCode as QrCodeIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Whatsapp as WhatsAppIcon,
  PictureAsPdf as PdfIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  MoreVert as MoreVertIcon,
  ChevronLeftRounded as ChevronLeftIcon,
  ChevronRightRounded as ChevronRightIcon,
  Calculate as CalculateIcon,
  WarningAmberRounded as WarningIcon,
  TaskAltRounded as SettleIcon,
  LockRounded as LockIcon,
} from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { usePawnMode } from "../../Context/PawnModeContext";
import { useSecretRevealTap, SECRET_TAP_SX } from "./secretRevealTap";
// Shared interest/insurance engine — a mirror of the backend's calculator.
// The backend preview is authoritative; this local copy is the offline/instant
// fallback so the teller still sees correct figures if the API is unreachable.
import {
  computeDues,
  allocatePayment,
} from "../../utils/pawnInterestCalculator";
// Price breakdown: a customer-facing explanation of how the amount was reached
// (which rule set the months charged, the arithmetic, how the money is applied).
import ReceiptBreakdownDialog from "./ReceiptBreakdownDialog";
import { buildReceiptBreakdown } from "./receiptBreakdown";
// Reuse the exact customer-creation OTP dialog for settlement authorisation —
// same send/verify endpoints and the same 123456 test bypass.
import OTPDialog from "./pawnCustomer/OTPDialog";
import PawnNavButtons from "./pawnNavButtons";
import {
  usePawnEditAccess,
  useViewOnlyGuard,
  VIEW_ONLY_MESSAGE,
} from "./pawnEditAccess";
// Mobile-number lookup shared with the Payment screen: the header search box
// also searches customers, and picking one loads their last transaction here.
import {
  useCustomerTxnLookup,
  CustomerTxnPopper,
} from "./customerTxnLookup";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// Government-approved official monthly interest cap (%). In OFFICIAL mode a loan
// charging above this is displayed as the cap (1.16%); the true rate is only
// revealed via the secret F2+O gesture. Mirrors the backend OFFICIAL_RATE_CAP.
const OFFICIAL_RATE_CAP = 1.16;

// ─── Accent tokens ───────────────────────────────────────────────────────────
// This sheet is mounted twice: /pawn/receipt (blue) posts ordinary repayment
// receipts, and /pawn/settlement (green) is the dedicated close-out screen —
// same form, same engine, different chrome so the teller can never mistake one
// for the other. Every accent below therefore resolves through a CSS custom
// property rather than a fixed hex, so the whole screen re-skins from the one
// `accentVars` block applied at render. The vars are published on :root (see
// the <GlobalStyles> in the render) so portalled dialogs, menus and poppers —
// which live outside this component's DOM subtree — pick them up too.
const ACCENT = "var(--pawn-accent)";
const ACCENT_DARK = "var(--pawn-accent-dark)";
const ACCENT_MED = "var(--pawn-accent-med)";
const ACCENT_LIGHT = "var(--pawn-accent-light)";
const ACCENT_PALE = "var(--pawn-accent-pale)";
const ACCENT_SUBTLE = "var(--pawn-accent-subtle)";

// The real hex behind those vars, per screen. MUI's own colour maths (alpha(),
// getContrastRatio()) can't parse a var(), so the theme below is built from
// these literals while everything in `sx` reads the var.
const ACCENT_RECEIPT = {
  main: "#2563EB",
  dark: "#1D4ED8",
  med: "#3B82F6",
  light: "#DBEAFE",
  pale: "#EFF6FF",
  subtle: "#F0F7FF",
  shadow: "rgba(37,99,235,0.30)",
  shadowSoft: "rgba(37,99,235,0.22)",
};
const ACCENT_SETTLEMENT = {
  main: "#059669",
  dark: "#047857",
  med: "#10B981",
  light: "#D1FAE5",
  pale: "#ECFDF5",
  subtle: "#F0FDF4",
  shadow: "rgba(5,150,105,0.30)",
  shadowSoft: "rgba(5,150,105,0.22)",
};

const accentVars = (a) => ({
  "--pawn-accent": a.main,
  "--pawn-accent-dark": a.dark,
  "--pawn-accent-med": a.med,
  "--pawn-accent-light": a.light,
  "--pawn-accent-pale": a.pale,
  "--pawn-accent-subtle": a.subtle,
  "--pawn-accent-shadow": a.shadow,
});

const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const BORDER_MED = "#CBD5E1";
const SURFACE = "#FFFFFF";
const BG = "#F8FAFC";
const BG_ALT = "#F1F5F9";

const SUCCESS = "#059669";
const SUCCESS_BG = "#ECFDF5";
const ERROR_C = "#DC2626";
const ERROR_BG = "#FEF2F2";
const WARN = "#D97706";
const INFO_C = "#0284C7";

// ─── Field styling ───────────────────────────────────────────────────────────
// Hide the native number-input spinner arrows (up/down steppers) on all number
// fields — mirrors the Payment screen. Kept as its own top-level selector keys
// so per-field "& .MuiInputBase-input" overrides can't clobber it. Paired with
// a non-passive wheel listener (below) that stops scroll from changing values.
const noSpinnerSx = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
};

const fieldSx = {
  ...noSpinnerSx,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: SURFACE,
    fontSize: "0.875rem",

    "& fieldset": {
      borderColor: "#9CA3AF",
      borderWidth: "2px",
    },

    "&:hover fieldset": {
      borderColor: ACCENT_MED,
      borderWidth: "2.5px",
    },

    "&.Mui-focused fieldset": {
      borderColor: ACCENT,
      borderWidth: "3px",
    },
  },

  "& .MuiInputLabel-root": {
    color: INK_MUTED,
    fontSize: "0.8rem",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: ACCENT_DARK,
  },
};

const readOnlyFieldSx = {
  ...fieldSx,

  "& .MuiInputBase-input": {
    cursor: "default",
    fontWeight: 700,
  },

  "& textarea": {
    cursor: "default",
    fontWeight: 700,
  },

  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],

    "&:hover fieldset": {
      borderColor: "#9CA3AF",
      borderWidth: "2px",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#9CA3AF",
      borderWidth: "2px",
    },
  },
};

const selectSx = {
  borderRadius: "8px",
  bgcolor: SURFACE,
  fontSize: "0.875rem",
  color: INK,
  ".MuiOutlinedInput-notchedOutline": {
    borderColor: "#9CA3AF",
    borderWidth: "2px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: ACCENT_MED,
    borderWidth: "2.5px",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ACCENT,
    borderWidth: "3px",
  },
};

// ─── Ticket-navigation chevron button styling ────────────────────────────────
const navBtnSx = {
  border: `1.5px solid ${BORDER_MED}`,
  borderRadius: "8px",
  color: ACCENT,
  p: "3px",
  bgcolor: SURFACE,
  "&:hover": { bgcolor: ACCENT_PALE, borderColor: ACCENT_MED },
  "&.Mui-disabled": { color: BORDER_MED, borderColor: BORDER },
};

// Built per accent (real hex, not var()) — MUI derives hover/disabled shades
// from palette.primary with colour maths that a CSS variable would break.
const makeAppTheme = (a) =>
  createTheme({
    palette: {
      primary: {
        main: a.main,
        dark: a.dark,
        light: a.light,
        contrastText: "#fff",
      },
      background: { default: BG, paper: SURFACE },
    },
    shape: { borderRadius: 10 },
    typography: { fontFamily: '"DM Sans", "Segoe UI", "Roboto", sans-serif' },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 8,
            padding: "7px 18px",
            transition: "all 0.18s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: `0 4px 14px ${a.shadowSoft}`,
            },
          },
          containedPrimary: {
            background: a.main,
            color: "#fff",
            "&:hover": { background: a.dark },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            boxShadow: "0 1px 4px rgba(15,23,42,0.07)",
            border: `1px solid ${BORDER}`,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 700,
            fontSize: "0.72rem",
            letterSpacing: "0.06em",
          },
        },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 600, fontSize: "0.75rem" } },
      },
    },
  });

const receiptTheme = makeAppTheme(ACCENT_RECEIPT);
const settlementTheme = makeAppTheme(ACCENT_SETTLEMENT);

const SectionHeader = ({ icon, title, action }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 1.5,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 3, height: 18, bgcolor: ACCENT, borderRadius: 4 }} />
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.875rem",
          color: INK,
          letterSpacing: "-0.01em",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <span style={{ fontSize: "0.95rem" }}>{icon}</span>
        {title}
      </Typography>
    </Box>
    {action}
  </Box>
);

const SectionCard = ({ icon, title, children, action, noPad }) => (
  <Box sx={{ mb: 2.5 }}>
    <SectionHeader icon={icon} title={title} action={action} />
    <Box
      sx={{
        bgcolor: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        p: noPad ? 0 : 2,
        boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
        overflow: "hidden",
      }}
    >
      {children}
    </Box>
  </Box>
);

const ImageSourceDialog = ({ open, index, prefix = "", onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{ sx: { borderRadius: 3, width: "100%", maxWidth: 300 } }}
  >
    <DialogTitle
      sx={{ textAlign: "center", pb: 1, fontSize: "1rem", fontWeight: 700 }}
    >
      Image Source
    </DialogTitle>
    <DialogContent sx={{ pb: 2 }}>
      <Stack spacing={1.5}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<CameraAltRoundedIcon />}
          onClick={() => {
            document.getElementById(`${prefix}camera-input-${index}`)?.click();
            onClose();
          }}
          sx={{ py: 1.5, borderRadius: 2 }}
        >
          Take Photo
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ImageIcon />}
          onClick={() => {
            document.getElementById(`${prefix}gallery-input-${index}`)?.click();
            onClose();
          }}
          sx={{ py: 1.5, borderRadius: 2 }}
        >
          Choose from Gallery
        </Button>
      </Stack>
    </DialogContent>
  </Dialog>
);

// ─── MAIN RECEIPT / SETTLEMENT SCREEN ────────────────────────────────────────
// One sheet, two mounts (see the accent tokens at the top of this file):
//
//   settlement = false → /pawn/receipt    — ordinary repayment receipts. The
//                                           teller enters whatever the customer
//                                           pays; the loan stays open.
//   settlement = true  → /pawn/settlement — the close-out counter. Settlement
//                                           mode is ON the moment the screen
//                                           loads, so the total auto-fills to
//                                           the FULL outstanding balance, the
//                                           receipt is numbered "H…" and the
//                                           loan is stamped settled on save.
//
// Settlement used to be a toggle button on the receipt screen, shown only when
// the remaining principal had fallen below a configured threshold. That hid the
// option exactly when a customer wanted to clear a large loan in one lump sum,
// so it was replaced by this dedicated screen (and the threshold setting went
// away with it).
// A pristine receipt form. A factory, not a constant, because todayDate has to
// be re-read whenever the screen is cleared — and because the reset below must
// restore EXACTLY the shape useState started with, not a hand-copied subset.
const blankReceiptForm = () => ({
  // Customer Details
  customerName: "",
  customerId: "",
  relativeRelation: "",
  relativeName: "",
  mobile: "",
  address: "",
  // Customer Images (URLs)
  customerPhotoUrl: null,
  idProofPhotoUrl: null,
  // Loan Details
  loanDate: "",
  loanAmount: 0,
  loanInterestRate: 0,
  schemeName: "",
  // Payment Details
  todayDate: dayjs().format("YYYY-MM-DD"),
  // "Interest paid till" — computed by the backend engine per the store's
  // grace/half-month/monthly rules (NOT a blind +1 month).
  interestUpToDate: "",
  interestAmount: 0,
  insuranceAmount: 0,
  interestDiscount: 0,
  otherCharges: 0,
  totalAmountReceived: 0,
  payMode: "CASH",
  chequeNo: "",
  cardNumber: "",
  expiryDate: "",
  cardHolderName: "",
  utrNumber: "",
  remarks: "",
});

// Money is rounded to paise wherever it is split or re-combined, so a total
// re-derived from its parts can never drift by a floating-point tail.
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const PawnReceiptScreen = ({ settlement = false }) => {
  const { currentStoreInfo, userInfo, defaultFY, defaultFYId, branchScope } =
    useContext(AutoTaskAuthContext) || {};
  const { mode: pawnMode, modeLoaded } = usePawnMode() || {};
  const storeId = currentStoreInfo?.id;
  const userId = userInfo?.id;
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // ── States ─────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [searchVoucher, setSearchVoucher] = useState("");
  const [searching, setSearching] = useState(false);
  // Voucher of the loan currently loaded.
  const [currentVoucher, setCurrentVoucher] = useState("");
  // True while a chevron step is resolving (BE lookup + loan load + receipt open).
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [loanData, setLoanData] = useState(null);
  const [ornaments, setOrnaments] = useState([]);
  const [pastReceipts, setPastReceipts] = useState([]);
  const [previousLoans, setPreviousLoans] = useState([]);
  // Pawn-shop settings that drive interest calculation. Fetched from the same
  // endpoint the Payment screen uses; each flag changes how interest is derived.
  const [pawnSettings, setPawnSettings] = useState(null);
  // Bank/UPI accounts (for non-cash receipt legs).
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState("");
  // ── Split payment (same feature as the Payment screen) ─────────────────────
  // When on, the receipt total can arrive as cash + up to two bank/account
  // transfers. Cash is the balancing leg; the legs always sum to the total.
  const [splitPayment, setSplitPayment] = useState(false);
  const [secondAccountId, setSecondAccountId] = useState("");
  const [cashAmount, setCashAmount] = useState("");
  const [bankAmount, setBankAmount] = useState("");
  // Optional second bank/account-transfer leg (revealed via "Add Another").
  const [secondBankEnabled, setSecondBankEnabled] = useState(false);
  const [thirdAccountId, setThirdAccountId] = useState("");
  const [bankAmount2, setBankAmount2] = useState("");
  // Reference numbers for the split legs: transfer 1 → transaction_ref_no_1,
  // transfer 2 → transaction_ref_no_2 (distinct from formData.utrNumber,
  // which belongs to the non-split UPI pay mode).
  const [splitRef1, setSplitRef1] = useState("");
  const [splitRef2, setSplitRef2] = useState("");
  // Live interest figures from the backend engine (authoritative). Populated by
  // the /receipts/calculate-interest preview so what the teller sees == what is
  // booked by createReceipt (both share the same server-side engine).
  const [interestPreview, setInterestPreview] = useState(null);
  const [calcInterestLoading, setCalcInterestLoading] = useState(false);
  // Raw loan / scheme / receipts-summary snapshots for the local fallback
  // engine (refs so fetchInterestPreview never closes over stale state).
  const loanDataRef = useRef(null);
  const schemeInfoRef = useRef(null);
  const receiptsSummaryRef = useRef(null);
  // Preview requests overlap — loading a loan asks for today's dues, and opening
  // one of its receipts immediately asks again for that receipt's own date with
  // itself excluded. Whichever was asked for LAST must win, regardless of which
  // reply lands first, or the form shows figures the save won't book.
  const previewSeqRef = useRef(0);
  const [saving, setSaving] = useState(false);
  // Secret reveal of the true interest rate for above-cap loans (F2+O gesture,
  // or a double tap on the Interest (%) label on handhelds).
  const [revealInterest, setRevealInterest] = useState(false);
  const revealTapHandlers = useSecretRevealTap(
    useCallback(() => setRevealInterest((v) => !v), []),
  );
  // "Which receipt to print?" popup, shown when a loan has multiple receipts.
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  // Receipt being edited (clicked in the Past Receipts rail). null = creating
  // a new receipt. Save switches to PUT /receipts/:id while this is set. Any
  // receipt may be edited — the backend replays the loan's whole receipt
  // chain afterwards so later receipts' figures and ledgers stay consistent.
  const [editingReceipt, setEditingReceipt] = useState(null);
  // The money applied to the LOAN (interest + insurance + principal, gross of
  // discount) for the receipt being edited — captured at load, held constant
  // while editing. Total = editBaseAmount + Other Charges − Interest Discount,
  // so adding Other Charges raises the total (and the split legs) on top of the
  // unchanged loan payment, instead of silently re-slicing it.
  const [editBaseAmount, setEditBaseAmount] = useState(0);
  // Manual override of "Total Amount Received": the teller can enter ANY amount
  // — including LESS than the interest due (a partial payment). When set, the
  // derived total stops auto-recomputing from the dues; instead it tracks
  // manualBase (the loan-facing amount the teller chose) + Other Charges −
  // Discount. Reset whenever fresh dues arrive (load / date change / edit / save).
  const [totalManual, setTotalManual] = useState(false);
  const [manualBase, setManualBase] = useState(0);
  // How much of "Other Charges" was auto-filled because the entered Total ran
  // past what the loan itself can absorb (see applyEnteredTotal). Kept apart
  // from the charges the teller typed by hand, so the Total can be edited back
  // DOWN without wiping a deliberate charge, and so a stale overflow is never
  // carried into a freshly-priced total. A ref, not state: every reader is an
  // event handler that needs the value as of NOW, and nothing renders from it
  // (what the teller sees is formData.otherCharges, auto share included).
  const autoChargesRef = useRef(0);
  // Drop the manual total and go back to deriving it from the dues. Any
  // auto-filled overflow goes with it — that money only ever existed as the
  // tail of the manual figure being dropped, so leaving it in Other Charges
  // would inflate the re-derived total. Callers that assign Other Charges
  // themselves in the same breath pass keepCharges: theirs is the authority.
  const resetManualTotal = useCallback(({ keepCharges = false } = {}) => {
    setTotalManual(false);
    setManualBase(0);
    const auto = autoChargesRef.current;
    autoChargesRef.current = 0;
    if (keepCharges || auto <= 0.01) return;
    setFormData((prev) => ({
      ...prev,
      otherCharges: Math.max(
        0,
        round2((Number(prev.otherCharges) || 0) - auto),
      ),
    }));
  }, []);
  // ── Settled-loan lock ──────────────────────────────────────────────────────
  // Once a loan is settled (is_settled = "Y") every receipt against it is
  // view-only: it can't be edited and no new receipt can be raised. Mirrors the
  // "payment locked once a receipt exists" rule on the Payment screen. Derived
  // straight off the loaded loan row.
  const receiptLocked = String(loanData?.is_settled).toUpperCase() === "Y";
  // Store Staff may raise a new receipt / settlement but not change one already
  // on file: a past receipt opened from the rail is view-only for them, with no
  // Update button. Kept apart from receiptLocked because the banners differ.
  const canEditRecords = usePawnEditAccess();
  const receiptRoleLocked =
    !!editingReceipt && !canEditRecords && !receiptLocked;
  const receiptViewOnly = receiptLocked || receiptRoleLocked;
  const leftColumnGuard = useViewOnlyGuard(receiptViewOnly);

  // ── Settlement mode ────────────────────────────────────────────────────────
  // 0 = an ordinary receipt; 1 = this receipt CLOSES the loan (backend numbers
  // its voucher "H…" and stamps is_settled / settled_date / settled_by).
  // It is simply "am I the settlement screen?", with two carve-outs:
  //   • editing a past receipt — that receipt is priced with itself excluded, so
  //     its balance is a historical one and must never close the loan;
  //   • an already-settled loan — nothing more to settle, the sheet is read-only.
  const settlementMode = settlement && !editingReceipt && !receiptLocked ? 1 : 0;
  // OTP-authorisation dialog for settlements (reuses the customer-creation
  // OTP flow). Opened on Save when settlement mode is on AND the store has
  // "OTP validation for settlement" enabled; the actual save runs only after
  // the customer's mobile OTP is verified.
  const [settlementOtpOpen, setSettlementOtpOpen] = useState(false);
  const [sendingSettlementOtp, setSendingSettlementOtp] = useState(false);
  // Under-payment confirmation: set (with the allocation the engine would make)
  // when Save is pressed for less than the dues, so the teller sees what stays
  // unpaid and carries forward before it is booked. null = nothing pending.
  const [shortfallPrompt, setShortfallPrompt] = useState(null);
  // "New" confirmation: true while asking whether to abandon whatever is on the
  // sheet and start a blank one. Only raised when there is something to lose, so
  // pressing New on an already-empty screen is a single click.
  const [newEntryConfirm, setNewEntryConfirm] = useState(false);
  // "Price Breakdown" dialog — the customer-facing explanation of the figure.
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  // Id of the receipt whose full record is currently being fetched (on click),
  // so the clicked card can show a spinner while the detail request is inflight.
  const [loadingReceiptId, setLoadingReceiptId] = useState(null);
  // Voucher of the past loan currently being loaded (clicked in the Past Loans
  // rail), so that card can show a spinner while its full lookup is inflight.
  const [loadingPrevVoucher, setLoadingPrevVoucher] = useState(null);

  const [formData, setFormData] = useState(blankReceiptForm);

  const [verificationImages, setVerificationImages] = useState({
    fingerprint1: null,
    fingerprint2: null,
    articlePhoto: null,
  });
  const [imageSourceDialog, setImageSourceDialog] = useState({
    open: false,
    index: null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const handleCloseSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  // ─── Secret gesture: F2 + O toggles the true interest rate reveal ──────────
  // For above-cap loans in official mode the UI shows 1.16%; holding F2 and
  // pressing O flips between the masked cap and the real rate. Tracked as a
  // key set so the two keys can be pressed in either order.
  const pressedKeys = useRef(new Set());
  useEffect(() => {
    const norm = (e) =>
      e.key && e.key.length === 1 ? e.key.toLowerCase() : e.key;
    const onKeyDown = (e) => {
      const k = norm(e);
      pressedKeys.current.add(k);
      if (pressedKeys.current.has("F2") && pressedKeys.current.has("o")) {
        e.preventDefault();
        setRevealInterest((v) => !v);
        // Consume the O so key-repeat doesn't rapidly re-toggle.
        pressedKeys.current.delete("o");
      }
    };
    const onKeyUp = (e) => pressedKeys.current.delete(norm(e));
    const clear = () => pressedKeys.current.clear();
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", clear);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", clear);
    };
  }, []);

  // Re-mask whenever the operator switches global pawn mode.
  useEffect(() => {
    setRevealInterest(false);
  }, [pawnMode]);

  // ── Abrupt mode switch with a loan / receipt already open ──────────────────
  // Same rule as the Payment screen (see the long note there): status 3
  // (official-clean) is SHARED by both books, so an official loan can be open
  // on this counter when the operator flips the mode with the hidden F12
  // gesture. Editing it from the other book is a trap — the backend derives a
  // receipt's status from the LOAN's own series and re-derives it per row on
  // save, so the same Update can move rows between the books under the
  // operator's feet. Wipe the sheet back to a fresh entry instead, exactly as
  // if New had been pressed; the loan can be pulled up again in the new mode
  // if that mode is allowed to see it.
  const loadedInModeRef = useRef(null);
  useEffect(() => {
    const prevMode = loadedInModeRef.current;
    // Track the live mode first — the persisted mode settles asynchronously.
    if (modeLoaded) loadedInModeRef.current = pawnMode;
    if (!modeLoaded) return;
    if (prevMode === null || prevMode === pawnMode) return;
    // Nothing pulled up yet: nothing to protect.
    if (!currentVoucher && !loanDataRef.current && !editingReceipt) return;
    // A pending "start a new entry?" confirm belongs to the loan going away.
    setNewEntryConfirm(false);
    clearScreenForNewEntry({ modeSwitched: true });
    // Keyed on the mode alone; the ref guard makes this fire once per switch and
    // the closure reads the state of the render that follows it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pawnMode, modeLoaded]);

  // Mouse-wheel over a focused number input must scroll the page, not change
  // the value (the spinner arrows are hidden via noSpinnerSx; this kills the
  // wheel increment). Mirrors the Payment screen.
  useEffect(() => {
    const stopNumberWheel = (e) => {
      const t = e.target;
      if (
        t instanceof HTMLInputElement &&
        t.type === "number" &&
        document.activeElement === t
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener("wheel", stopNumberWheel, { passive: false });
    return () => document.removeEventListener("wheel", stopNumberWheel);
  }, []);

  // ─── Helper: fetch pawn settings (interest-calc options) ───────────────────
  // Options like one_month_deduct, no_interest_upto_2_days, pawn_monthly_interest,
  // pawn_compound_interest and the <15-day full/half-month rules each change how
  // the interest amount is computed for a repayment.
  const fetchPawnSettings = useCallback(async () => {
    if (storeId === undefined || storeId === null) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/settings/${storeId}`,
      );
      if (res.data.success) {
        setPawnSettings(res.data.data);
      } else {
        setPawnSettings(null);
        showSnackbar(
          res.data.statusMessage || "Failed to load settings",
          "error",
        );
      }
    } catch (error) {
      console.error("Failed to fetch pawn settings:", error);
      setPawnSettings(null);
      showSnackbar("Failed to load pawn settings", "error");
    }
  }, [storeId]);

  useEffect(() => {
    fetchPawnSettings();
  }, [fetchPawnSettings]);

  // ─── Helper: fetch bank/UPI accounts (for non-cash receipt legs) ───────────
  const fetchBankAccounts = useCallback(async () => {
    if (storeId === undefined || storeId === null) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/accounts/accounts/${storeId}/accounts-only`,
      );
      if (res.data.success && Array.isArray(res.data.data)) {
        // Exclude the internal pawn accounts (pledge/interest/insurance) — those
        // are the credit side of a receipt, never where cash/UPI is received.
        const excludeIds = [
          pawnSettings?.pledge_acc,
          pawnSettings?.interest_acc,
          pawnSettings?.insurance_acc,
        ].filter(Boolean);
        setBankAccounts(
          res.data.data.filter((acc) => !excludeIds.includes(acc.account_id)),
        );
      } else {
        setBankAccounts([]);
      }
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
      setBankAccounts([]);
    }
  }, [storeId, pawnSettings]);

  useEffect(() => {
    fetchBankAccounts();
  }, [fetchBankAccounts]);

  // ─── Split-payment helpers (mirrors the Payment screen) ────────────────────
  const getCashAccountId = useCallback(
    () => pawnSettings?.cash_acc || null,
    [pawnSettings],
  );

  // The UPI-transfer account from Pawn Settings is only the DEFAULT for the
  // first account-transfer leg — the user can pick any other bank.
  const getUpiAccountId = useCallback(
    () => pawnSettings?.UPI_acc || null,
    [pawnSettings],
  );

  // Accounts offered for the transfer legs (bankAccounts already excludes
  // pledge/interest/insurance; also hide the other-charges account).
  const getFilteredAccounts = useCallback(() => {
    if (!pawnSettings || !bankAccounts.length) return bankAccounts;
    return bankAccounts.filter(
      (acc) => acc.account_id !== pawnSettings.other_charges_acc,
    );
  }, [bankAccounts, pawnSettings]);

  // The split legs must sum to the money actually handed over — the
  // teller-entered Total Amount Received.
  const getSplitTotal = () =>
    Math.max(0, Number(formData.totalAmountReceived) || 0);

  const handleCashAmountChange = (value) => {
    const total = getSplitTotal();
    const cash = Math.min(Math.max(0, parseFloat(value) || 0), total);
    setCashAmount(cash);
    setBankAmount(total - cash);
  };

  const handleBankAmountChange = (value) => {
    const total = getSplitTotal();
    const bank = Math.min(Math.max(0, parseFloat(value) || 0), total);
    setBankAmount(bank);
    setCashAmount(total - bank);
  };

  // Three-leg variants (cash + bank-1 + bank-2): cash is the balancing leg, so
  // entering a bank amount reduces cash to keep the legs summing to the total.
  const handleCash3 = (value) => {
    const total = getSplitTotal();
    const b2 = parseFloat(bankAmount2) || 0;
    const cash = Math.min(Math.max(0, parseFloat(value) || 0), total - b2);
    setCashAmount(cash);
    setBankAmount(Math.max(0, total - cash - b2)); // bank-1 absorbs the rest
  };
  const handleBank1_3 = (value) => {
    const total = getSplitTotal();
    const b2 = parseFloat(bankAmount2) || 0;
    const b1 = Math.min(Math.max(0, parseFloat(value) || 0), total - b2);
    setBankAmount(b1);
    setCashAmount(Math.max(0, total - b1 - b2)); // cash decreases to balance
  };
  const handleBank2_3 = (value) => {
    const total = getSplitTotal();
    const b1 = parseFloat(bankAmount) || 0;
    const b2 = Math.min(Math.max(0, parseFloat(value) || 0), total - b1);
    setBankAmount2(b2);
    setCashAmount(Math.max(0, total - b1 - b2)); // cash decreases to balance
  };

  // Reveal / hide the optional second bank leg.
  const handleAddSecondBank = () => {
    const cashAccId = getCashAccountId();
    const used = [secondAccountId, cashAccId].filter(Boolean);
    const nextAccount = getFilteredAccounts().find(
      (acc) => acc.account_id !== cashAccId && !used.includes(acc.account_id),
    );
    if (nextAccount) setThirdAccountId(nextAccount.account_id);
    setSecondBankEnabled(true);
  };
  const handleRemoveSecondBank = () => {
    setSecondBankEnabled(false);
    setThirdAccountId("");
    setBankAmount2("");
    setSplitRef2("");
  };

  // Clear the whole split allocation (toggle-off, new loan, after save).
  const resetSplitState = useCallback(() => {
    setSplitPayment(false);
    setCashAmount("");
    setBankAmount("");
    setBankAmount2("");
    setSecondAccountId("");
    setSecondBankEnabled(false);
    setThirdAccountId("");
    setSplitRef1("");
    setSplitRef2("");
  }, []);

  // When the underlying total changes while split is on, re-balance the legs so
  // they keep summing to the total. The difference is absorbed by the CASH leg
  // (the balancing leg) so the bank/transfer allocations the teller set survive
  // — e.g. adding ₹500 Other Charges raises cash by ₹500 and leaves the two
  // bank legs untouched. Only if cash can't cover the shortfall do we fall back
  // to all-cash.
  useEffect(() => {
    if (!splitPayment) return;
    const total = Math.max(0, Number(formData.totalAmountReceived) || 0);
    const cAmt = parseFloat(cashAmount) || 0;
    const bAmt = parseFloat(bankAmount) || 0;
    const b2Amt = parseFloat(bankAmount2) || 0;
    // Same ±0.01 tolerance the backend applies to the legs, so loading a
    // stored split receipt never trips a spurious re-balance.
    if (Math.abs(cAmt + bAmt + b2Amt - total) <= 0.01 || total <= 0) return;
    const newCash = total - bAmt - b2Amt;
    if (newCash >= 0) {
      setCashAmount(newCash);
    } else {
      setCashAmount(total);
      setBankAmount(0);
      setBankAmount2(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.totalAmountReceived, splitPayment]);

  // ─── Local fallback preview (shared engine, mirrors the backend) ───────────
  // Used only when the preview API is unreachable. Builds the loan STATE from
  // the loaded loan row + the receipts summary (revised principal, carryovers,
  // paid-up-to of the last receipt) and runs the same calculation locally.
  const buildLocalPreview = useCallback(
    (receiptDate) => {
      const loan = loanDataRef.current;
      if (!loan || !pawnSettings) return null;
      const summary = receiptsSummaryRef.current;
      try {
        const dues = computeDues(
          {
            principal:
              summary?.principal_balance != null
                ? Number(summary.principal_balance)
                : Number(loan.loan_amount) || 0,
            loanDate: loan.transaction_date,
            interestRate: loan.loan_interest,
            insuranceRate: loan.insurance_percentage,
            carryoverInterest: Number(summary?.pending_interest) || 0,
            carryoverInsurance: Number(summary?.pending_insurance) || 0,
            lastPaidUpToDate: summary?.paid_up_to_date || null,
            oneMonthDeduct: loan.one_month_deduct,
          },
          receiptDate,
          pawnSettings,
          schemeInfoRef.current,
        );
        return {
          loan_amount: Number(loan.loan_amount) || 0,
          loan_interest: Number(loan.loan_interest) || 0,
          period_interest: dues.periodInterest,
          period_insurance: dues.periodInsurance,
          pending_interest: dues.carryoverInterestBefore,
          pending_insurance: dues.carryoverInsuranceBefore,
          interest_receivable: dues.interestDue,
          insurance_receivable: dues.insuranceDue,
          total_due: dues.interestDue + dues.insuranceDue,
          principal_balance: dues.principal,
          paid_up_to_date: dues.paidUpToDate,
          reference_date: dues.referenceDate,
          due_date: dues.dueDate,
          months_charged: dues.monthsCharged,
          days: dues.daysCharged,
          // The date being priced — the breakdown quotes it as "Paying on".
          upto_date: receiptDate,
          applied_interest_rate: dues.appliedInterestRate,
          applied_insurance_rate: dues.appliedInsuranceRate,
          calc_branch: dues.branch,
          // Audit trail behind the figure — drives the price breakdown, same
          // field names the backend preview returns.
          loan_date: dues.loanDate,
          full_months: dues.fullMonths,
          extra_days: dues.extraDays,
          half_extra_days: dues.halfExtraDays,
          one_month_deduct: dues.oneMonthDeduct,
          rules: dues.rules,
          _local: true, // computed on-device, not by the server
        };
      } catch (e) {
        console.error("Local interest fallback failed:", e);
        return null;
      }
    },
    [pawnSettings],
  );

  // ─── Helper: interest preview (authoritative, from the backend engine) ─────
  // Same engine createReceipt uses, so the figure shown == the figure booked.
  // `receiptDate` is the date the customer is paying; the backend derives the
  // dues AND the "interest paid till" date from it (grace / half-month /
  // monthly precedence — never a blind +1 month).
  // `keepTotal` is set while EDITING a receipt: the dues are re-priced for the
  // new date, but the money the customer actually handed over must not move —
  // the whole point is to expose a gap between the two (see the shortfall guard
  // in handleSaveReceipt).
  const applyPreview = useCallback((d, { keepTotal = false } = {}) => {
    setInterestPreview(d);
    // Fresh dues ⇒ drop any manual total override so the suggested total tracks
    // the new interest/insurance figures again.
    if (!keepTotal) resetManualTotal();
    setFormData((prev) => ({
      ...prev,
      interestAmount: Number(d.interest_receivable) || 0,
      insuranceAmount: Number(d.insurance_receivable) || 0,
      interestUpToDate: d.paid_up_to_date || "",
    }));
  }, [resetManualTotal]);

  // `excludeReceiptId` prices an EDIT: the backend replays the loan's receipt
  // chain up to that receipt's slot (for the date being previewed) instead of
  // starting from the latest snapshot — which would include the very receipt
  // being edited. Without it an edit could not be previewed at all, and a
  // re-dated receipt was re-priced silently by the server on save.
  const fetchInterestPreview = useCallback(
    async (voucherNo, receiptDate, excludeReceiptId = null) => {
      if (!storeId || !voucherNo) return;
      const editing = !!excludeReceiptId;
      const seq = ++previewSeqRef.current;
      const superseded = () => seq !== previewSeqRef.current;
      setCalcInterestLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/receipts/calculate-interest`,
          {
            params: {
              store_id: storeId,
              voucher_no: voucherNo,
              upto_date: receiptDate,
              ...(editing ? { exclude_receipt_id: excludeReceiptId } : {}),
            },
          },
        );
        if (superseded()) return; // a newer preview was asked for meanwhile
        if (res.data.success) {
          // An older backend ignores exclude_receipt_id and would answer with
          // the state AFTER the receipt being edited — figures that look
          // plausible but are not what the update books. It echoes the id back
          // when it honoured it, so anything else is treated as "can't price
          // this edit" rather than shown.
          if (
            editing &&
            Number(res.data.data?.excluded_receipt_id) !==
              Number(excludeReceiptId)
          ) {
            setInterestPreview(null);
            showSnackbar(
              "This server can't re-price a receipt being edited — update the backend before editing receipt dates",
              "warning",
            );
            return;
          }
          applyPreview(res.data.data, { keepTotal: editing });
        } else {
          setInterestPreview(null);
          showSnackbar(
            res.data.statusMessage || "Failed to calculate interest",
            "warning",
          );
        }
      } catch (error) {
        console.error("Failed to calculate interest:", error);
        // Offline / server unreachable — fall back to the shared local engine.
        // Only for a NEW receipt: the local fallback builds its state from the
        // LATEST receipt's summary, which is the wrong starting point for an
        // edit (only the server can replay the chain to that receipt's slot).
        if (superseded()) return;
        const local = editing ? null : buildLocalPreview(receiptDate);
        if (local) {
          applyPreview(local);
          showSnackbar(
            "Server unreachable — interest computed locally (verify on save)",
            "warning",
          );
        } else {
          setInterestPreview(null);
          if (editing)
            showSnackbar(
              "Couldn't re-price this receipt — check the connection before saving",
              "warning",
            );
        }
      } finally {
        if (!superseded()) setCalcInterestLoading(false);
      }
    },
    [storeId, applyPreview, buildLocalPreview],
  );

  // ─── Helper: fetch prior receipts for a loan ───────────────────────────────
  const fetchReceipts = useCallback(
    async (voucherNo) => {
      if (!storeId || !voucherNo) return;
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/receipts/${storeId}/${encodeURIComponent(
            voucherNo,
          )}`,
        );
        if (res.data.success && Array.isArray(res.data.data?.receipts)) {
          // Summary (current principal, carryovers, paid-up-to of the last
          // receipt) feeds the local fallback engine.
          receiptsSummaryRef.current = res.data.data.summary || null;
          // Lightweight card data only — the full row (payment legs, refs,
          // remarks) is fetched on demand via fetchReceiptById when a receipt
          // is opened for edit or print.
          setPastReceipts(
            res.data.data.receipts.map((r) => ({
              id: r.id,
              receipt_no: r.voucher_ref || r.id,
              amount: Number(r.amount) || 0,
              date: r.receipt_date,
              // Time the payment was taken ("HH:mm:ss") — used to order two
              // receipts paid on the same day and shown on the card in 12h.
              time: r.payment_time || null,
              interest_deducted: Number(r.interest_deducted) || 0,
              insurance_deducted: Number(r.insurance_deducted) || 0,
              principal_reduction: Number(r.principal_reduction) || 0,
              paid_up_to_date: r.paid_up_to_date || null,
              revised_principal: Number(r.revised_principal) || 0,
            })),
          );
        } else {
          receiptsSummaryRef.current = null;
          setPastReceipts([]);
        }
      } catch (error) {
        console.error("Failed to fetch receipts:", error);
        setPastReceipts([]);
      }
    },
    [storeId],
  );

  // ─── Helper: fetch ONE receipt in full (on demand, for edit/print) ─────────
  // The list only carries card fields; the whole row (legs, refs, remarks) is
  // pulled here the moment a receipt is opened.
  const fetchReceiptById = useCallback(
    async (id) => {
      if (!id) return null;
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/receipts/by-id/${id}`,
          { params: { store_id: storeId } },
        );
        if (res.data.success && res.data.data?.receipt) {
          return res.data.data.receipt;
        }
        showSnackbar(
          res.data.statusMessage || "Failed to load receipt",
          "error",
        );
        return null;
      } catch (error) {
        console.error("Failed to fetch receipt:", error);
        showSnackbar("Failed to load receipt", "error");
        return null;
      }
    },
    [storeId],
  );

  // ─── Helper: fetch loan by voucher number (fully integrated) ───────────────
  const fetchLoanByVoucher = useCallback(
    async (voucherNo, { boxValue } = {}) => {
      if (!voucherNo || voucherNo.trim() === "") {
        showSnackbar("Please enter a voucher number", "warning");
        return false;
      }
      setSearching(true);
      try {
        const loanRes = await axios.get(
          `${API_BASE_URL}/api/pawn/payments/loan/${encodeURIComponent(
            voucherNo,
          )}`,
        );
        if (!loanRes.data.success)
          throw new Error(loanRes.data.message || "Loan not found");

        const {
          loan_info,
          customer_info,
          ornaments_info: orns,
          nominee_info,
          scheme_info,
          previousLoans: prevLoans,
        } = loanRes.data.data;

        // Enforce the active pawn mode, using the SAME visibility rule as the
        // dashboard, the reports and the Past Loans list below: status 3
        // (official-clean) is SHARED by both books, so official mode loads
        // status 1 + 3 and unofficial mode loads status 2 + 3. Only status 1 is
        // exclusive to official mode. A voucher the active mode may not see
        // reads as "not found". Receipts raised against it stay in the right
        // books either way — the BE derives a receipt's status from the LOAN's
        // own series, not from the app mode.
        const recStatus = Number(loan_info?.status) || 1;
        const activeMode = Number(pawnMode) === 2 ? 2 : 1;
        const visibleInMode =
          activeMode === 2
            ? recStatus === 2 || recStatus === 3
            : recStatus !== 2;
        if (!visibleInMode) {
          showSnackbar(`Voucher ${voucherNo} not found`, "error");
          return false;
        }

        setLoanData(loan_info);
        loanDataRef.current = loan_info;
        // Scheme row (tiers included when the loan is on a scheme) — used by
        // the local fallback engine for tier-rate selection. Its `interest` has
        // already been resolved for the loan's mode by the backend (unofficial
        // loans carry the scheme's Interest 2 rate), so the fallback preview
        // prices exactly like the API.
        schemeInfoRef.current = scheme_info || null;
        setOrnaments(orns || []);
        // Past-loans sidebar: status 3 (official-clean) is shared between the
        // books, so official mode lists status 1 + 3 and unofficial mode lists
        // status 2 + 3.
        const wantUnofficial = activeMode === 2;
        setPreviousLoans(
          (Array.isArray(prevLoans) ? prevLoans : []).filter((l) => {
            const st = Number(l?.status);
            return wantUnofficial ? st === 2 || st === 3 : st !== 2;
          }),
        );
        // Article photo comes straight off the loan record (base64/URL).
        setVerificationImages({
          fingerprint1: null,
          fingerprint2: null,
          articlePhoto: loan_info?.article_img_1 || null,
        });

        let customerPhotoUrl = null;
        let idProofPhotoUrl = null;
        if (customer_info.documents && Array.isArray(customer_info.documents)) {
          const photoDoc = customer_info.documents.find(
            (d) => d.name === "Photo",
          );
          if (photoDoc && photoDoc.file && photoDoc.file !== "No file") {
            customerPhotoUrl = photoDoc.file;
          }
          const idDoc =
            customer_info.documents.find((d) => d.name === "Aadhar") ||
            customer_info.documents.find((d) => d.name === "PAN");
          if (idDoc && idDoc.file && idDoc.file !== "No file") {
            idProofPhotoUrl = idDoc.file;
          }
        }

        // Scheme name comes directly from the response's scheme_info (present
        // only when the loan actually has a scheme).
        const schemeName = scheme_info?.scheme_name || "";

        const loanAmt = parseFloat(loan_info.loan_amount) || 0;
        const ratePerMonth = parseFloat(loan_info.loan_interest) || 0;
        const todayStr = dayjs().format("YYYY-MM-DD");

        setFormData((prev) => ({
          ...prev,
          customerName: customer_info?.name || "",
          customerId: customer_info?.id?.toString() || "",
          relativeRelation: customer_info?.relative_relation || "",
          relativeName: customer_info?.relative_name || "",
          mobile: customer_info?.mobile || "",
          address: customer_info?.pickup_address || "", // <-- NEW: populate address
          customerPhotoUrl,
          idProofPhotoUrl,
          loanDate: loan_info.transaction_date
            ? dayjs(loan_info.transaction_date).format("YYYY-MM-DD")
            : "",
          loanAmount: loanAmt,
          loanInterestRate: ratePerMonth,
          schemeName,
          todayDate: todayStr,
          // Filled in by the preview (paid_up_to_date from the engine).
          interestUpToDate: "",
          // Interest/insurance are filled in by the backend preview
          // (fetchInterestPreview); reset so a stale value never lingers.
          interestAmount: 0,
          insuranceAmount: 0,
          interestDiscount: 0,
          otherCharges: 0,
          totalAmountReceived: 0,
          payMode: "CASH",
          chequeNo: "",
          cardNumber: "",
          expiryDate: "",
          cardHolderName: "",
          utrNumber: "",
          remarks: "",
        }));
        setInterestPreview(null);
        setSelectedBankAccount("");
        resetSplitState();
        // Loading a loan always lands in "new receipt" mode.
        setEditingReceipt(null);
        setEditBaseAmount(0);
        resetManualTotal({ keepCharges: true });
        // A freshly-loaded above-cap loan starts masked until F2+O is pressed.
        setRevealInterest(false);
        // Remember the backend's canonical voucher (not the string the user
        // typed) so the chevrons step from a value the navigate parser accepts.
        const canonicalVoucher = loan_info?.voucher_no ?? voucherNo;
        // A caller already on its way to open a receipt passes that receipt's
        // number as `boxValue`, so the box lands on its final value in one go.
        // Writing the loan voucher here first would leave it on screen for the
        // two requests that still follow (fetchReceipts, then fetchReceiptById
        // inside startEditReceipt) — long enough to read as a visible flash of
        // the wrong number before the receipt number wiped it.
        setSearchVoucher(boxValue == null ? canonicalVoucher : String(boxValue));
        setCurrentVoucher(canonicalVoucher);
        // Prior receipts FIRST (their summary feeds the offline fallback),
        // then the authoritative interest figure for today's date.
        await fetchReceipts(canonicalVoucher);
        fetchInterestPreview(canonicalVoucher, todayStr);
        showSnackbar("Loan details loaded successfully", "success");
        return true;
      } catch (error) {
        console.error("Error fetching loan:", error);
        showSnackbar(
          error.response?.data?.message ||
            error.message ||
            "Failed to load loan",
          "error",
        );
        return false;
      } finally {
        setSearching(false);
      }
    },
    [
      fetchInterestPreview,
      fetchReceipts,
      pawnMode,
      resetSplitState,
      resetManualTotal,
    ],
  );


  // Interest & insurance amounts are authoritative from the backend preview
  // (fetchInterestPreview), so there is no client-side recalculation here.
  // Total is still derived locally: interest + insurance + charges − discount.
  useEffect(() => {
    // Base = the money going to the loan. New receipt: interest + insurance
    // (from the preview). Editing: the captured editBaseAmount, which already
    // includes the receipt's principal component (a plain sum would drop it).
    // Either way Other Charges add on top and Interest Discount reduces, so
    // entering Other Charges while editing raises the total — and forces the
    // split legs to be re-balanced — rather than silently re-slicing the loan
    // payment (which broke debit=credit on the ledger).
    // Base = the money going to the loan. Manual override (teller typed a total,
    // possibly a partial interest payment) wins; else editing uses the loaded
    // receipt's base; else a new receipt suggests interest + insurance. Other
    // Charges add on top and Interest Discount reduces in every case.
    // Settlement mode pays the loan off in full, so the base also includes the
    // remaining principal (interest + insurance + principal_balance) — the total
    // auto-fills to the whole outstanding balance. A manual override still wins.
    const settlementBase =
      settlementMode === 1 && interestPreview
        ? formData.interestAmount +
          formData.insuranceAmount +
          (Number(interestPreview.principal_balance) || 0)
        : null;
    const base = totalManual
      ? manualBase
      : editingReceipt
      ? editBaseAmount
      : settlementBase != null
      ? settlementBase
      : formData.interestAmount + formData.insuranceAmount;
    const total = base + formData.otherCharges - formData.interestDiscount;
    setFormData((prev) => ({
      ...prev,
      totalAmountReceived: total < 0 ? 0 : total,
    }));
  }, [
    formData.interestAmount,
    formData.insuranceAmount,
    formData.interestDiscount,
    formData.otherCharges,
    editingReceipt,
    editBaseAmount,
    totalManual,
    manualBase,
    settlementMode,
    interestPreview,
  ]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  // Receipt date is editable; the dues AND the "interest paid till" date are
  // re-priced by the engine for the new payment date (the grace / half-month /
  // full-month precedence can change the figure day by day).
  const handleReceiptDateChange = (e) => {
    const newDate = e.target.value;
    const parsed = dayjs(newDate);
    setFormData((prev) => ({
      ...prev,
      todayDate: newDate,
    }));
    // Editing re-prices too — the day the receipt is dated decides which rule
    // applies (grace / half-month / full extra month), so moving it by a single
    // day can change the dues. The preview excludes the receipt being edited so
    // the figure matches exactly what the update will book.
    if (currentVoucher && parsed.isValid()) {
      fetchInterestPreview(currentVoucher, newDate, editingReceipt?.id || null);
    }
  };

  // What the LOAN itself can absorb from this receipt, gross of any discount:
  // interest + insurance + the outstanding principal. Money beyond it is not a
  // loan payment at all — the engine hands it back as `excess_amount` and posts
  // it nowhere. Returns null when the dues aren't known (no preview), so callers
  // skip the rule instead of inventing a ceiling.
  //
  // Correct while EDITING too: that preview is replayed to the receipt's own
  // slot, so principal_balance is the balance BEFORE it — precisely what that
  // one receipt is allowed to absorb.
  const getLoanAbsorbable = () => {
    if (!interestPreview) return null;
    return round2(
      (Number(formData.interestAmount) || 0) +
        (Number(formData.insuranceAmount) || 0) +
        (Number(interestPreview.principal_balance) || 0),
    );
  };

  // Full outstanding balance that must be collected to CLOSE the loan in
  // settlement mode — the same absorbable figure, which is why settling is
  // simply "collect the whole ceiling". Callers add Other Charges on top and
  // subtract any Interest Discount. Returns null when not settling (or no dues
  // loaded), so guards can be skipped cleanly.
  const getSettlementBaseDue = () =>
    settlementMode === 1 ? getLoanAbsorbable() : null;

  // Typing is never fought mid-keystroke. Entering 93000 against a ₹92,700
  // settlement floor passes through 9 → 93 → 930 → 9300, every one of them
  // below the floor; clamping here would snap the field back on the first digit
  // and the amount could never be typed at all. So this records exactly what
  // was entered, and the floor is applied on blur (below) once the teller has
  // finished — with the Save guard as the real backstop either way.
  //
  // The CEILING, unlike the floor, can be applied per keystroke without ever
  // fighting the typing: going over it doesn't reject the figure, the surplus
  // simply lands in Other Charges and the Total stands exactly as typed.
  const handleTotalAmountManualChange = (e) => {
    let val = parseFloat(e.target.value);
    if (isNaN(val)) val = 0;
    if (val < 0) val = 0;
    applyEnteredTotal(val);
  };

  // ─── Over-collection → Other Charges ──────────────────────────────────────
  // The engine allocates a receipt interest → insurance → principal and takes
  // Other Charges off the top; whatever is left once the principal is cleared
  // comes back as `excess_amount` and is posted to NO account, while the ledger
  // still debits the full money legs — so an over-collection would leave the
  // voucher short by exactly that excess. Money above what the loan can absorb
  // is therefore booked as what it actually is, a charge: it goes straight into
  // Other Charges, which credits the other-charges account and keeps the entry
  // balanced. Nothing is refused and nothing has to be re-typed — the customer
  // hands over ₹5,500 against ₹5,350 of dues, the ₹150 becomes Other Charges,
  // and the Total still reads ₹5,500.
  //
  // Returns the auto-filled charge, so a caller can say where the money went.
  // `pin` records the manual base — the keystroke path always does, that being
  // what lets a partial payment stand; the blur backstop passes false so merely
  // tabbing through the field pins nothing.
  const applyEnteredTotal = (val, { pin = true } = {}) => {
    const discount = Number(formData.interestDiscount) || 0;
    // Only the overflow share is ours to move: the rest of the box is a charge
    // the teller entered deliberately and must survive the reconciliation.
    let typedCharges = Math.max(
      0,
      round2((Number(formData.otherCharges) || 0) - autoChargesRef.current),
    );
    // Charges can never exceed the money collected, or the derived total would
    // bounce straight back above the figure just typed.
    if (typedCharges > val) typedCharges = val;
    // Freeze the loan-facing base (entered total − Other Charges + Discount) so
    // subsequent Charges/Discount edits adjust the total on top of the amount
    // the teller chose to collect, and the derived-total effect stops resetting
    // it back to the full dues. This is what enables partial interest payments.
    let base = round2(val - typedCharges + discount);
    // Settlement mode is left out on purpose: there Total and Other Charges are
    // reconciled against the payoff FLOOR on blur (handleTotalAmountBlur), and
    // a per-keystroke rule would fight it.
    const absorbable = settlementMode === 1 ? null : getLoanAbsorbable();
    let auto = 0;
    if (absorbable !== null && base > absorbable + 0.01) {
      auto = round2(base - absorbable);
      base = absorbable;
    }
    // Nothing to move and nothing to hand back ⇒ leave the form untouched.
    if (!pin && auto <= 0.01 && autoChargesRef.current <= 0.01) return 0;
    autoChargesRef.current = auto;
    setManualBase(Math.max(0, base));
    setTotalManual(true);
    setFormData((prev) => ({
      ...prev,
      otherCharges: round2(typedCharges + auto),
      totalAmountReceived: val,
    }));
    return auto;
  };

  // Settlement total, reconciled once the field is left rather than per
  // keystroke. On the settlement screen the identity always holds:
  //
  //     Total = payoff (interest + insurance + principal − discount)
  //             + Other Charges
  //
  // so Total and Other Charges are two views of the same figure. Typing a Total
  // ABOVE the payoff therefore doesn't create a loose surplus — the difference
  // IS the shop's charge, and it is written into Other Charges so it reaches the
  // other-charges account. (Money above what a receipt can allocate comes back
  // from the engine as `excess_amount` and is posted to no account at all, which
  // would leave the voucher short by exactly that much.) Typing BELOW the payoff
  // is the one thing that can't stand: it would close the loan with a balance
  // still owing, behind a settled lock that blocks any correction.
  const handleTotalAmountBlur = () => {
    const settlementBase = getSettlementBaseDue();
    if (settlementBase === null) {
      reconcileReceiptOverflow();
      return;
    }
    const discount = Number(formData.interestDiscount) || 0;
    // The charges-FREE floor: what has to reach the loan itself to close it.
    const payoff = Math.max(0, settlementBase - discount);
    const entered = Number(formData.totalAmountReceived) || 0;
    // Total is fully derived from payoff + charges again, so the manual
    // override must come off — otherwise it would keep pinning the old figure.
    // Other Charges is assigned below, so it is not the reset's to adjust.
    resetManualTotal({ keepCharges: true });

    if (entered + 0.01 < payoff) {
      showSnackbar(
        `Settlement requires the full balance of ₹${payoff.toLocaleString(
          "en-IN",
        )}. Enter more than that and the extra becomes Other Charges — you just can't collect less.`,
        "warning",
      );
      setFormData((prev) => ({
        ...prev,
        otherCharges: 0,
        totalAmountReceived: payoff,
      }));
      return;
    }

    const surplus = Math.round((entered - payoff) * 100) / 100;
    const prevCharges = Number(formData.otherCharges) || 0;
    setFormData((prev) => ({
      ...prev,
      otherCharges: surplus,
      totalAmountReceived: entered,
    }));
    // Say where the money went, so the rule is learnt rather than guessed at.
    if (Math.abs(surplus - prevCharges) > 0.01) {
      showSnackbar(
        surplus > 0
          ? `₹${surplus.toLocaleString(
              "en-IN",
            )} above the ₹${payoff.toLocaleString(
              "en-IN",
            )} balance moved into Other Charges.`
          : `Other Charges cleared — ₹${payoff.toLocaleString(
              "en-IN",
            )} covers the balance exactly.`,
        "info",
      );
    }
  };

  // Blur backstop for an ordinary receipt: reconcile whatever ended up in the
  // field — including a value that arrived by paste, arrow key or any path
  // other than the keystroke handler — and then, once rather than per
  // keystroke, say where the surplus went, so the rule is learnt instead of
  // guessed at.
  const reconcileReceiptOverflow = () => {
    const auto = applyEnteredTotal(Number(formData.totalAmountReceived) || 0, {
      pin: false,
    });
    if (auto <= 0.01) return;
    // What the loan actually took, in money: the absorbable gross less the
    // discount written off it.
    const loanShare = Math.max(
      0,
      round2(
        (getLoanAbsorbable() || 0) - (Number(formData.interestDiscount) || 0),
      ),
    );
    showSnackbar(
      `₹${auto.toLocaleString(
        "en-IN",
      )} more than this receipt can apply to the loan (₹${loanShare.toLocaleString(
        "en-IN",
      )}) moved into Other Charges.`,
      "info",
    );
  };

  // ─── Edit an existing receipt (clicked in the Past Receipts rail) ──────────
  // The card only holds summary fields, so fetch the full row first, then
  // populate the form from it.
  const startEditReceipt = async (rec, voucherOverride = null) => {
    if (loadingReceiptId) return; // a fetch is already inflight
    // A settled loan's receipts can still be OPENED to view their details, but
    // never edited/saved — Save stays disabled and the form shows a view banner.
    const viewOnly =
      String(loanDataRef.current?.is_settled).toUpperCase() === "Y";
    setLoadingReceiptId(rec.id);
    const raw = await fetchReceiptById(rec.id);
    setLoadingReceiptId(null);
    if (!raw) return;
    setEditingReceipt({ id: rec.id, receipt_no: rec.receipt_no });
    // The box tracks the row actually open, so each chevron step visibly moves.
    // The loan's own ticket number stays on show in the header caption.
    setSearchVoucher(String(rec.receipt_no ?? ""));
    // Loading a receipt is a fresh base — clear any manual total override.
    resetManualTotal({ keepCharges: true });
    // Money applied to the loan (interest + insurance + principal), gross of
    // discount: amount − other_charges + interest_discount. Held constant so
    // the total tracks Other Charges / Discount edits without losing principal.
    setEditBaseAmount(
      (Number(raw.amount) || 0) -
        (Number(raw.other_charges) || 0) +
        (Number(raw.interest_discount) || 0),
    );
    setFormData((prev) => ({
      ...prev,
      todayDate: raw.receipt_date
        ? dayjs(raw.receipt_date).format("YYYY-MM-DD")
        : prev.todayDate,
      interestUpToDate: raw.paid_up_to_date || "",
      interestAmount: Number(raw.interest_deducted) || 0,
      insuranceAmount: Number(raw.insurance_deducted) || 0,
      interestDiscount: Number(raw.interest_discount) || 0,
      otherCharges: Number(raw.other_charges) || 0,
      totalAmountReceived: Number(raw.amount) || 0,
      payMode: "CASH",
      chequeNo: raw.transaction_ref || "",
      cardNumber: "",
      expiryDate: "",
      cardHolderName: "",
      utrNumber: "",
      remarks: raw.remarks || "",
    }));
    // Money legs: cash-only rows load as plain cash; anything with a bank leg
    // loads in split mode so the stored allocation round-trips exactly.
    const cash = Number(raw.cash_amount_received) || 0;
    const b1 = Number(raw.card_amount_received_1) || 0;
    const b2 = Number(raw.card_amount_received_2) || 0;
    resetSplitState();
    setSelectedBankAccount("");
    if (b1 > 0 || b2 > 0) {
      setSplitPayment(true);
      setCashAmount(cash);
      setBankAmount(b1);
      setSecondAccountId(raw.bank_account_id_1 || "");
      setSplitRef1(raw.transaction_ref_no_1 || "");
      if (b2 > 0) {
        setSecondBankEnabled(true);
        setThirdAccountId(raw.bank_account_id_2 || "");
        setBankAmount2(b2);
        setSplitRef2(raw.transaction_ref_no_2 || "");
      }
    }
    // Re-price the receipt LIVE against its own date, with itself excluded from
    // the state it is priced from. This is what the update will book, so the
    // Interest/Insurance fields (and the price breakdown) show the real dues for
    // that date — including when they no longer match the money on the receipt.
    // `voucherOverride` is how the chevron walk passes the loan it has just
    // loaded: its setCurrentVoucher hasn't reached this closure yet.
    const voucherForPreview = voucherOverride || currentVoucher;
    if (voucherForPreview) {
      fetchInterestPreview(
        voucherForPreview,
        raw.receipt_date
          ? dayjs(raw.receipt_date).format("YYYY-MM-DD")
          : formData.todayDate,
        rec.id,
      );
    }
    showSnackbar(
      viewOnly
        ? `Viewing receipt #${rec.receipt_no} — loan is settled, so it's read-only`
        : !canEditRecords
        ? `Viewing receipt #${rec.receipt_no} — read only`
        : `Editing receipt #${rec.receipt_no} — make your changes and press Update Receipt`,
      "info",
    );
  };

  // Leave edit mode and restore the normal "new receipt for today" form.
  const cancelEditReceipt = () => {
    setEditingReceipt(null);
    // No receipt open any more ⇒ the box falls back to the loan it belongs to.
    setSearchVoucher(currentVoucher || "");
    setEditBaseAmount(0);
    resetManualTotal({ keepCharges: true });
    resetSplitState();
    setSelectedBankAccount("");
    const todayStr = dayjs().format("YYYY-MM-DD");
    setFormData((prev) => ({
      ...prev,
      todayDate: todayStr,
      interestUpToDate: "",
      interestAmount: 0,
      insuranceAmount: 0,
      interestDiscount: 0,
      otherCharges: 0,
      totalAmountReceived: 0,
      payMode: "CASH",
      chequeNo: "",
      cardNumber: "",
      expiryDate: "",
      cardHolderName: "",
      utrNumber: "",
      remarks: "",
    }));
    if (currentVoucher) fetchInterestPreview(currentVoucher, todayStr);
  };

  // ─── Unified search: LOAN voucher OR RECEIPT / SETTLEMENT number ───────────
  // Three things can be typed here, and the box itself is auto-filled with the
  // second and third by the chevrons:
  //   • a loan voucher      ⇒ load the loan, ready for a new receipt
  //   • a "REC…" ledger ref ⇒ load its loan AND open that receipt
  //   • an "H…" ledger ref  ⇒ same, for a settlement (its own SETV series)
  //
  // Opening the receipt matters now that the chevrons park a receipt number in
  // this box: pressing Enter on what is already shown has to give that receipt
  // back, not silently swap it for a blank one on its loan.
  const handleSearchSubmit = async (raw) => {
    const input = String(raw ?? "").trim();
    if (!input) {
      showSnackbar("Please enter a loan or receipt number", "warning");
      return;
    }
    // "REC" is the hardcoded repayment series, so it can never be a loan
    // voucher — a failed lookup there is a real error. "H…" is only a HINT: a
    // store whose loan prefix begins with H would collide, so an unresolved H
    // falls through to the loan lookup instead of erroring out.
    const isReceiptRef = /^rec/i.test(input);
    const isSettlementRef = /^h\d+$/i.test(input);
    if ((isReceiptRef || isSettlementRef) && storeId != null) {
      setSearching(true);
      let hit = null;
      let failMsg = null;
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/receipts/resolve/${storeId}/${encodeURIComponent(
            input,
          )}`,
        );
        if (res.data?.success && res.data.data?.voucher_no) hit = res.data.data;
        else failMsg = res.data?.statusMessage || `No receipt "${input}" found`;
      } catch (error) {
        console.error("Failed to resolve receipt:", error);
        failMsg = "Failed to look up receipt number";
      } finally {
        setSearching(false);
      }
      if (hit) {
        // Load the parent loan first (that resets the sheet), then open the
        // receipt on top of it — handing over its loan voucher explicitly,
        // since setCurrentVoucher hasn't reached this closure yet.
        const receiptNo = hit.voucher_ref || hit.receipt_id;
        const loaded = await fetchLoanByVoucher(hit.voucher_no, {
          boxValue: receiptNo,
        });
        if (loaded) {
          await startEditReceipt(
            { id: hit.receipt_id, receipt_no: receiptNo },
            hit.voucher_no,
          );
        }
        return;
      }
      if (isReceiptRef) {
        showSnackbar(failMsg, "error");
        return;
      }
      // An "H…" that resolved to nothing: fall through and try it as a loan.
    }
    await fetchLoanByVoucher(input);
  };

  // ─── Auto-open a receipt handed over by navigation state ───────────────────
  // The dashboard's "Recent Receipts" list links here with the loan voucher AND
  // the receipt's own ledger ref. The ref is what gets submitted: it goes through
  // the same unified search a teller types into, so the loan loads and THAT
  // receipt opens on top of it. Handing over only the loan voucher would open a
  // blank NEW receipt against the loan instead of the row that was clicked.
  //
  // Gated on the prerequisites the lookup itself needs (store + settings, which
  // drive the interest preview) and replayed exactly once per navigation.
  const autoOpenedRef = useRef(null);
  useEffect(() => {
    const target = location.state?.receipt_ref || location.state?.voucher_no;
    if (!target) return;
    if (autoOpenedRef.current === target) return;
    if (storeId == null || !pawnSettings) return;
    autoOpenedRef.current = target;
    setSearchVoucher(target);
    handleSearchSubmit(target);
    // handleSearchSubmit is re-created every render, so it is deliberately not a
    // dependency — the once-only ref is what keeps this from re-firing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, storeId, pawnSettings]);

  // ─── Clear the whole screen, ready for a new entry ─────────────────────────
  // The counterpart of the Payment screen's "New": not just leaving receipt-edit
  // mode (that's cancelEditReceipt, which keeps the loan) but dropping the loan,
  // its ornaments, its receipts and every derived figure. Reached from the New
  // button (via handleNewEntry) and by stepping forward past the newest
  // receipt / settlement.
  //
  // pawnSettings and bankAccounts are deliberately kept — they are store-level
  // and re-fetching them would only stall the next lookup.
  // opts.modeSwitched — the clear was forced by an official/unofficial switch
  // rather than asked for, so the snackbar has to say why the sheet emptied.
  const clearScreenForNewEntry = (opts = {}) => {
    setSearchVoucher("");
    // Emptying the box has to take its customer dropdown with it, or a stale
    // suggestion list / "no record found" line hangs over the cleared sheet.
    customerLookup.reset();
    setCurrentVoucher("");
    setLoanData(null);
    loanDataRef.current = null;
    schemeInfoRef.current = null;
    receiptsSummaryRef.current = null;
    setOrnaments([]);
    setPastReceipts([]);
    setPreviousLoans([]);
    setInterestPreview(null);
    setEditingReceipt(null);
    setEditBaseAmount(0);
    resetManualTotal({ keepCharges: true });
    resetSplitState();
    setSelectedBankAccount("");
    setFormData(blankReceiptForm());
    setVerificationImages({
      fingerprint1: null,
      fingerprint2: null,
      articlePhoto: null,
    });
    // A cleared screen must not carry an above-cap reveal into the next loan.
    setRevealInterest(false);
    setLoadingReceiptId(null);
    setLoadingPrevVoucher(null);
    // Any dialog still keyed to the loan that just went away.
    setPrintDialogOpen(false);
    setBreakdownOpen(false);
    setShortfallPrompt(null);
    setSettlementOtpOpen(false);
    const what = settlement ? "settlement" : "receipt";
    showSnackbar(
      opts?.modeSwitched
        ? `Mode changed — screen cleared. Ready for a new ${what}.`
        : `Screen cleared. Ready for a new ${what}.`,
      "info",
    );
  };

  // ─── "New" button ──────────────────────────────────────────────────────────
  // The Payment screen's New, on this counter: wipe the sheet back to a blank
  // entry. Anything loaded or typed is worth a confirmation first — a loaded
  // loan, an open receipt edit, a half-typed search or a collected amount —
  // otherwise clear straight away.
  const handleNewEntry = () => {
    const hasWork =
      !!currentVoucher ||
      !!searchVoucher.trim() ||
      !!editingReceipt ||
      Number(formData.totalAmountReceived) > 0;
    if (hasWork) setNewEntryConfirm(true);
    else clearScreenForNewEntry();
  };

  // ─── Chevron navigation: walk RECEIPTS in creation order ───────────────────
  // The chevrons step through the register THIS screen owns — repayment receipts
  // on /pawn/receipt, close-out settlements on /pawn/settlement — never through
  // loans. dir = "prev" (left chevron) / "next" (right chevron), anchored on the
  // receipt currently open. With none open the form is on a fresh receipt, which
  // sits just past the newest one: "prev" lands on the last receipt raised at the
  // counter and "next" has nowhere to go (its chevron is hidden in that state).
  //
  // A receipt has no sequence of its own — the "REC…" / "H…" ledger ref is just
  // the order it was booked in — so creation date/time is the counter.
  const navigateTicket = async (dir) => {
    const forward = dir === "next";
    // Already on a fresh receipt: nothing exists past it. (The right chevron
    // isn't rendered here either.)
    if (forward && !editingReceipt) return;
    if (!storeId) return;
    setLoadingTicket(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/receipts/nav-step/${storeId}`,
        {
          params: {
            // The receipt the walk steps away from; omitted on a fresh form.
            current_id: editingReceipt?.id || undefined,
            mode: pawnMode,
            settlement: settlement ? 1 : 0,
            dir,
          },
        },
      );
      const hit = res.data?.success ? res.data.data : null;
      if (hit?.voucher_no) {
        // Load the receipt's own loan first — that resets the screen to "new
        // receipt" — then open the receipt on top of it. Its loan voucher is
        // handed over explicitly because setCurrentVoucher hasn't reached this
        // closure yet.
        const loaded = await fetchLoanByVoucher(hit.voucher_no, {
          boxValue: hit.receipt_no,
        });
        if (loaded) {
          await startEditReceipt(
            { id: hit.receipt_id, receipt_no: hit.receipt_no },
            hit.voucher_no,
          );
        }
      } else if (forward) {
        // Walked forward off the newest one. The only thing past it is the entry
        // about to be written, so clear the whole screen — same as the Payment
        // screen's right chevron. That also hides the right chevron again.
        clearScreenForNewEntry();
      } else {
        showSnackbar(
          res.data?.statusMessage ||
            `No earlier ${settlement ? "settlement" : "receipt"} found`,
          "info",
        );
      }
    } catch (err) {
      showSnackbar(
        err.response?.data?.statusMessage ||
          `Failed to navigate ${settlement ? "settlements" : "receipts"}`,
        "error",
      );
    } finally {
      setLoadingTicket(false);
    }
  };

  // ─── Mobile-number lookup in the header search box ────────────────────────
  // The box takes a loan / receipt number; typing DIGITS also searches customers
  // by mobile (name / party code match too, since that is one search) and drops
  // them under the box. Picking one loads that customer's most recent entry in
  // THIS screen's own register — the last repayment receipt on /pawn/receipt,
  // the last close-out on /pawn/settlement — and reports by name when they have
  // none. Opening it is the chevron walk's move exactly: load the parent loan
  // first (that resets the sheet), then the receipt on top of it.
  const customerLookup = useCustomerTxnLookup({
    storeId,
    pawnMode,
    kind: settlement ? "settlement" : "receipt",
    showSnackbar,
    onPick: async (txn) => {
      setSearchVoucher(txn.receipt_no);
      const loaded = await fetchLoanByVoucher(txn.voucher_no, {
        boxValue: txn.receipt_no,
      });
      if (loaded) {
        await startEditReceipt(
          { id: txn.receipt_id, receipt_no: txn.receipt_no },
          txn.voucher_no,
        );
      }
    },
  });

  // ─── Load a past loan from the Past Loans rail (click-to-switch) ────────────
  // Clicking one of the customer's other loans re-loads the whole screen for
  // that loan — so a receipt can be made for it without leaving the page. Reuses
  // the same lookup the voucher search uses; that resets edit/split/preview state
  // and re-fetches receipts + interest, so no extra teardown is needed here.
  const handleSelectPreviousLoan = async (loan) => {
    const voucher = loan?.voucher_no;
    if (!voucher) return;
    // Ignore clicks while any lookup is inflight, or when it's already loaded.
    if (searching || loadingTicket || loadingPrevVoucher) return;
    if (String(voucher) === String(currentVoucher)) {
      showSnackbar("This loan is already loaded", "info");
      return;
    }
    setLoadingPrevVoucher(voucher);
    try {
      await fetchLoanByVoucher(voucher);
      // Bring the loaded loan into view (the rail sits below the form on mobile).
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoadingPrevVoucher(null);
    }
  };

  // `otpVerified` is passed true only by the settlement OTP dialog's success
  // callback, letting the save proceed past the OTP gate below.
  // `shortfallAck` is passed true by the under-payment dialog's confirm button
  // (see the shortfall guard) — the teller has seen what will stay unpaid.
  const handleSaveReceipt = async (
    otpVerified = false,
    shortfallAck = false,
  ) => {
    if (!currentVoucher) {
      showSnackbar("Load a loan before saving a receipt", "warning");
      return;
    }
    if (!storeId) {
      showSnackbar("No store selected", "error");
      return;
    }
    // A settled loan is read-only — never create or edit a receipt against it.
    if (receiptLocked) {
      showSnackbar(
        "This loan is settled — its receipts are read-only",
        "error",
      );
      return;
    }
    if (editingReceipt && !canEditRecords) {
      showSnackbar(VIEW_ONLY_MESSAGE, "error");
      return;
    }
    const total = Number(formData.totalAmountReceived) || 0;
    if (total <= 0 && !settlementMode) {
      showSnackbar(
        "Total amount received must be greater than zero",
        "warning",
      );
      return;
    }

    const charges = Number(formData.otherCharges) || 0;
    const discount = Number(formData.interestDiscount) || 0;

    // Other Charges are credited to the configured other-charges account —
    // without one that credit leg is simply dropped and the voucher would not
    // net out. Worth guarding now that collecting above the dues fills the
    // field on its own (applyEnteredTotal), not only when a teller types in it.
    if (charges > 0 && !pawnSettings?.other_charges_acc) {
      showSnackbar(
        "Other Charges account is not configured. Set it in Pawn Settings before collecting more than the amount due.",
        "error",
      );
      return;
    }

    // ── Settlement under-collection guard (backstop to the field clamp) ──────
    // Settling closes the loan permanently; if the amount collected is below the
    // full outstanding balance the loan would be marked settled while a balance
    // is still pending — and the settled lock then blocks any correction. Block
    // that here as a safety net in case the total was set before Settle was
    // pressed or by any path other than the clamped Total field.
    const settlementBase = getSettlementBaseDue();
    if (settlementBase !== null && !editingReceipt) {
      const minTotal = Math.max(0, settlementBase + charges - discount);
      if (total + 0.01 < minTotal) {
        showSnackbar(
          `Cannot settle for less than the full balance of ₹${minTotal.toLocaleString(
            "en-IN",
          )}. Enter at least this amount (Other Charges may add to it).`,
          "error",
        );
        return;
      }
    }

    // receipt_amount = the TOTAL money the customer hands over (interest +
    // insurance + other charges + principal). The backend engine takes the
    // charges off the top and allocates the rest interest → insurance →
    // principal itself — the FE no longer sends a received_interest split.
    const receiptAmount = total;

    // Money legs: cash by default, else a single bank/UPI leg to the chosen
    // acct — or, when split payment is on, cash + up to two transfer legs.
    const isCash = formData.payMode === "CASH";
    if (!splitPayment && !isCash && !selectedBankAccount) {
      showSnackbar(
        "Select the bank/UPI account the money was received into",
        "warning",
      );
      return;
    }

    // ── Split-payment integrity checks (so the legs net out on the ledger) ──
    const splitCash = parseFloat(cashAmount) || 0;
    const splitBank1 = parseFloat(bankAmount) || 0;
    const splitBank2 = secondBankEnabled ? parseFloat(bankAmount2) || 0 : 0;
    if (splitPayment) {
      const sum = splitCash + splitBank1 + splitBank2;
      // The backend rejects legs that don't equal receipt_amount (±0.01).
      if (Math.abs(sum - total) > 0.01) {
        showSnackbar(
          `Split amounts must total ₹${total.toLocaleString(
            "en-IN",
          )} (currently ₹${sum.toLocaleString("en-IN")})`,
          "error",
        );
        return;
      }
      if (splitBank1 > 0 && !secondAccountId) {
        showSnackbar("Select an account for the first transfer", "error");
        return;
      }
      if (splitBank2 > 0 && !thirdAccountId) {
        showSnackbar("Select an account for the second transfer", "error");
        return;
      }
    }

    // ── Under-payment guard ──────────────────────────────────────────────────
    // The engine allocates interest → insurance → principal, so an amount below
    // the dues silently leaves the tail unpaid and carries it to the next
    // receipt. That is a legitimate partial payment — but it must never be a
    // surprise, and it is easiest to miss on an EDIT: re-dating a receipt can
    // move it past the grace window into a whole extra month, raising the dues
    // while the money on the receipt stays put. Show exactly what will happen
    // and make the teller confirm it (allocated by the same engine that books
    // it, so the dialog can't disagree with the result).
    const shortfallCheck =
      !settlementMode && interestPreview
        ? allocatePayment(
            {
              interestDue: Number(interestPreview.interest_receivable) || 0,
              insuranceDue: Number(interestPreview.insurance_receivable) || 0,
              principal: Number(interestPreview.principal_balance) || 0,
            },
            Math.max(0, total - charges),
            { interestDiscount: discount },
          )
        : null;
    const unpaidTail = shortfallCheck
      ? shortfallCheck.shortfallInterest + shortfallCheck.shortfallInsurance
      : 0;
    if (!shortfallAck && unpaidTail > 0.01) {
      setShortfallPrompt({
        editing: !!editingReceipt,
        receiptDate: formData.todayDate,
        interestDue: Number(interestPreview.interest_receivable) || 0,
        insuranceDue: Number(interestPreview.insurance_receivable) || 0,
        discount,
        charges,
        total,
        interestPaid: shortfallCheck.interestDeduction,
        insurancePaid: shortfallCheck.insuranceDeduction,
        principalPaid: shortfallCheck.principalReduction,
        shortfallInterest: shortfallCheck.shortfallInterest,
        shortfallInsurance: shortfallCheck.shortfallInsurance,
        otpVerified,
      });
      return;
    }

    const currentYear = defaultFY || new Date().getFullYear();

    const payload = {
      user_id: userId,
      store_id: storeId,
      // The branch that TOOK the money, which is deliberately not always the
      // branch that made the loan — a customer who pledged at one shop paying
      // at another is the flow branches were added for, and it is the reason
      // the receipt carries a branch of its own rather than inheriting the
      // loan's. Left off when the session has no branch: the server then falls
      // back to the LOAN's branch, which is a better guess than the main one.
      ...(branchScope?.mode === "branch" && branchScope?.branchId != null
        ? { branch_id: branchScope.branchId }
        : {}),
      voucher_no: currentVoucher,
      transaction_date: formData.todayDate,
      payment_time: dayjs().format("HH:mm:ss"),
      receipt_amount: receiptAmount,
      // No received_interest/received_insurance overrides: the backend engine
      // allocates interest → insurance → principal and computes paid-up-to.
      interest_discount: discount,
      other_charges: charges,
      remarks: formData.remarks || null,
      // Payment legs (mirror the pawn_broking model the backend derives from).
      // pay_mode always represents the CASH leg; bank/account-transfer legs go
      // in card_amount_received_1/2 with their accounts in bank_account_id_1/2.
      pay_mode: "CASH",
      cash_amount_received: splitPayment ? splitCash : isCash ? total : 0,
      card_amount_received_1: splitPayment ? splitBank1 : isCash ? 0 : total,
      card_amount_received_2: splitPayment ? splitBank2 : 0,
      bank_account_id_1: splitPayment
        ? splitBank1 > 0
          ? secondAccountId
          : null
        : isCash
        ? null
        : selectedBankAccount,
      bank_account_id_2: splitPayment && splitBank2 > 0 ? thirdAccountId : null,
      transaction_ref:
        formData.payMode === "CHEQUE" ? formData.chequeNo || null : null,
      // Split-leg references: one transaction reference per transfer leg.
      transaction_ref_no_1: splitPayment
        ? splitRef1 || null
        : formData.payMode === "UPI"
        ? formData.utrNumber || null
        : null,
      transaction_ref_no_2:
        splitPayment && secondBankEnabled ? splitRef2 || null : null,
      // Financial-year context (required by the backend for unofficial loans).
      fy_id: defaultFYId,
      finance_year: currentYear,
      // 1 ⇒ this receipt settles (closes) the loan: the backend numbers its
      // voucher "H…" and stamps the loan is_settled / settled_date / settled_by.
      settlement_mode: settlementMode === 1 ? 1 : 0,
    };

    // ── Settlement OTP gate ────────────────────────────────────────────────
    // When settling AND the store requires settlement OTP, defer the save:
    // send an OTP to the customer's mobile and open the verification dialog.
    // The dialog's success callback re-invokes this with otpVerified = true.
    const otpRequiredForSettlement = Boolean(pawnSettings?.OTP_for_settlement);
    if (
      settlementMode === 1 &&
      !editingReceipt &&
      otpRequiredForSettlement &&
      !otpVerified
    ) {
      const custMobile = String(formData.mobile || "").trim();
      if (!custMobile) {
        showSnackbar(
          "Customer has no mobile number on file for OTP verification",
          "error",
        );
        return;
      }
      setSendingSettlementOtp(true);
      try {
        const otpRes = await axios.post(
          `${API_BASE_URL}/api/pawn/customers/send-otp`,
          { mobile: custMobile, store_id: storeId, purpose: "settlement" },
        );
        if (otpRes.data?.success && otpRes.data.statusCode === 1) {
          setSettlementOtpOpen(true);
        } else {
          showSnackbar(
            otpRes.data?.statusMessage || "Failed to send OTP",
            "error",
          );
        }
      } catch (otpErr) {
        console.error("Failed to send settlement OTP:", otpErr);
        showSnackbar("Failed to send settlement OTP", "error");
      } finally {
        setSendingSettlementOtp(false);
      }
      return;
    }

    setSaving(true);
    try {
      // Edit mode rewrites the clicked receipt (and its ledger rows) in place;
      // otherwise a new receipt is created. Both share the same payload shape.
      const res = editingReceipt
        ? await axios.put(
            `${API_BASE_URL}/api/pawn/receipts/${editingReceipt.id}`,
            payload,
          )
        : await axios.post(`${API_BASE_URL}/api/pawn/receipts`, payload);
      if (res.data.success && res.data.statusCode === 200) {
        const booked = res.data.data || {};
        const paidTill = booked.paid_up_to_date;
        const verb = editingReceipt ? "updated" : "saved";
        // Report what the engine ACTUALLY booked, not what was asked for. Any
        // unpaid tail (interest/insurance that the amount didn't cover) carries
        // to the next receipt, so it is called out here rather than only living
        // in the row — the teller confirmed it, and now sees it confirmed back.
        const carried =
          (Number(booked.carryover_interest) || 0) +
          (Number(booked.carryover_insurance) || 0);
        const paidTillNote = paidTill
          ? ` — interest paid till ${dayjs(paidTill).format("DD MMM YYYY")}`
          : "";
        showSnackbar(
          carried > 0
            ? `Receipt ${verb}${paidTillNote}. ₹${carried.toLocaleString(
                "en-IN",
              )} still unpaid — it carries to the next receipt.`
            : `Receipt ${verb}${paidTillNote || " successfully!"}`,
          carried > 0 ? "warning" : "success",
        );
        resetSplitState();
        // A settlement receipt just closed this loan — reflect it locally so the
        // whole screen locks read-only (settlementMode falls back to 0 off
        // receiptLocked, so the save/OTP path can't fire again) even if the
        // teller navigates back to this loan without a full reload.
        if (settlementMode === 1 && !editingReceipt) {
          setLoanData((prev) => (prev ? { ...prev, is_settled: "Y" } : prev));
          if (loanDataRef.current) loanDataRef.current.is_settled = "Y";
        }
        // Open the pawn ticket generator pre-filled with the just-saved receipt,
        // mirroring the Payment screen's print-on-save flow. Updates stay on
        // this screen instead (the teller is usually fixing a mistake, not
        // printing) — the Print button covers re-printing.
        if (!editingReceipt) {
          const savedReceipt = res.data.data?.receipt;
          if (savedReceipt) {
            openReceiptTicket(savedReceipt);
            return;
          }
        }
        // Refresh in place; after an update, return to "new receipt for today".
        const nextDate = editingReceipt
          ? dayjs().format("YYYY-MM-DD")
          : formData.todayDate;
        setEditingReceipt(null);
        // Back to a new receipt ⇒ the box shows the loan again, not the receipt
        // number that was being edited.
        setSearchVoucher(currentVoucher || "");
        setEditBaseAmount(0);
        resetManualTotal({ keepCharges: true });
        await fetchReceipts(currentVoucher);
        await fetchInterestPreview(currentVoucher, nextDate);
        setFormData((prev) => ({
          ...prev,
          todayDate: nextDate,
          interestDiscount: 0,
          otherCharges: 0,
          remarks: "",
          chequeNo: "",
          cardNumber: "",
          expiryDate: "",
          cardHolderName: "",
          utrNumber: "",
        }));
        setSelectedBankAccount("");
      } else {
        showSnackbar(
          res.data.statusMessage ||
            `Failed to ${editingReceipt ? "update" : "save"} receipt`,
          "error",
        );
      }
    } catch (error) {
      console.error("Error saving receipt:", error);
      showSnackbar(
        error.response?.data?.statusMessage ||
          error.message ||
          `Failed to ${editingReceipt ? "update" : "save"} receipt`,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // Print a receipt's pawn ticket. With one receipt, go straight to the ticket
  // generator; with several, ask the operator which one first.
  const handlePrint = () => {
    if (!currentVoucher) {
      showSnackbar("Load a loan first", "info");
      return;
    }
    if (pastReceipts.length === 0) {
      showSnackbar("No receipts to print for this loan yet", "info");
      return;
    }
    // A receipt is already selected (loaded for edit) → print THAT one directly,
    // no picker. Only ask which receipt when none is selected.
    if (editingReceipt) {
      const rec = pastReceipts.find((r) => r.id === editingReceipt.id) || {
        id: editingReceipt.id,
      };
      printReceipt(rec);
      return;
    }
    // Nothing selected: one receipt prints straight away; several ⇒ ask which.
    if (pastReceipts.length === 1) {
      printReceipt(receiptsNewestFirst[0] || pastReceipts[0]);
      return;
    }
    setPrintDialogOpen(true);
  };

  // Print a card's ticket: fetch its full row on demand (the list is
  // lightweight), then open the pawn-ticket generator for it.
  const printReceipt = async (rec) => {
    if (!rec || loadingReceiptId) return;
    setLoadingReceiptId(rec.id);
    const raw = await fetchReceiptById(rec.id);
    setLoadingReceiptId(null);
    if (raw) openReceiptTicket(raw);
  };

  // ─── Official-mode interest-cap masking ────────────────────────────────────
  const isOfficialMode = Number(pawnMode) === 1;
  const trueInterestRate = Number(formData.loanInterestRate) || 0;
  const isAboveCap = trueInterestRate > OFFICIAL_RATE_CAP;
  // Above-cap loans in official mode display the cap (1.16%) instead of the true
  // rate, unless the operator has revealed it via the secret F2+O gesture.
  const maskInterest = isOfficialMode && isAboveCap && !revealInterest;
  // Mask any rate the same way the main field is masked (used for the loaded
  // loan and the past-loans sidebar so the real rate never leaks in official
  // mode until F2+O reveals it).
  const maskRate = (rate) => {
    const r = Number(rate) || 0;
    return isOfficialMode && r > OFFICIAL_RATE_CAP && !revealInterest
      ? OFFICIAL_RATE_CAP
      : r;
  };
  const displayInterestRate = maskRate(trueInterestRate);
  // The faint secret mark appears whenever the loan is above cap in official
  // mode — a signal only the informed operator will notice.
  const showSecretMark = isOfficialMode && isAboveCap;
  // The double-tap reveal is only armed while there is something to reveal, so
  // the label behaves like plain text on every other loan.
  const revealTap = showSecretMark ? revealTapHandlers : {};

  // ─── Build the pawn-ticket-generator payload for a saved receipt row ───────
  // Mirrors the Payment screen's ticketData shape but flags the ticket as a
  // RECEIPT and fills the money fields from the receipt (interest received,
  // charges, legs). The interest rate is masked the same way the screen masks
  // it so an official-mode ticket never reveals an above-cap rate.
  const buildReceiptTicketData = (receipt) => {
    const cash = Number(receipt.cash_amount_received) || 0;
    const bank =
      (Number(receipt.card_amount_received_1) || 0) +
      (Number(receipt.card_amount_received_2) || 0);
    const totalReceived = cash + bank;
    const interestPaid = Number(receipt.interest_deducted) || 0;
    const interestDiscount = Number(receipt.interest_discount) || 0;
    const insurancePaid = Number(receipt.insurance_deducted) || 0;
    const charges = Number(receipt.other_charges) || 0;
    const principalPaid = Number(receipt.principal_reduction) || 0;

    const paymentMethod =
      bank > 0 && cash > 0
        ? "Cash + Bank/UPI"
        : bank > 0
        ? "Bank / UPI"
        : "Cash";

    const formattedOrnaments = ornaments.map((item, idx) => ({
      id: idx + 1,
      description: item.item_name || "",
      metal: "",
      purity: item.purity_percentage || "",
      rate: Number(item.metal_type_rate) || 0,
      pcs: Number(item.item_pcs) || 1,
      weight: Number(item.item_net) || 0,
      grossWeight: Number(item.item_gross) || 0,
      value: Number(item.item_value) || 0,
    }));

    const principalTotal =
      Number(loanData?.loan_amount) || Number(formData.loanAmount) || 0;
    const ornamentValue = ornaments.reduce(
      (s, o) => s + (Number(o.item_value) || 0),
      0,
    );
    // The receipt row snapshots the outstanding principal AFTER itself.
    const principalBalanceAfter =
      receipt.revised_principal != null
        ? Number(receipt.revised_principal)
        : Math.max(0, principalTotal - principalPaid);

    // A receipt that clears the outstanding principal is a SETTLEMENT (full
    // redemption) — it prints under the "S" template; a partial/interest-only
    // receipt prints under "R". This drives which ticket template loads.
    const settled = principalTotal > 0 && principalBalanceAfter <= 0.005;
    const ticketTypeCode = settled ? "S" : "R";

    return {
      type: settled ? "settlement" : "receipt",
      storeInfo: {
        name: currentStoreInfo?.name || "Pawn Shop",
        address: currentStoreInfo?.address || "",
        phone: currentStoreInfo?.owner_mobile || "",
        email: currentStoreInfo?.email || "",
        gst: currentStoreInfo?.gst_no || "",
      },
      ticketInfo: {
        // A receipt ticket must show the RECEIPT's own voucher number, which
        // lives in voucher_ref — NOT the pawn/loan voucher (voucher_no). Falling
        // back to voucher_no/currentVoucher only when voucher_ref is missing.
        voucherNo: receipt.voucher_ref || receipt.voucher_no || currentVoucher,
        date: receipt.receipt_date,
        time: receipt.payment_time || dayjs().format("HH:mm:ss"),
      },
      customerInfo: {
        id: formData.customerId,
        name: formData.customerName,
        mobile: formData.mobile,
        address: formData.address,
        sonOf: formData.relativeRelation
          ? `${formData.relativeRelation} ${formData.relativeName}`
          : formData.relativeName,
      },
      loanInfo: {
        loanAmount: principalTotal,
        itemValue: ornamentValue,
        interestRate: maskRate(
          loanData?.loan_interest ?? formData.loanInterestRate,
        ),
        interestAmount: interestPaid,
        insuranceAmount: insurancePaid,
        // "Interest paid till" — the engine-computed settled-through date.
        interestPaidTill: receipt.paid_up_to_date || null,
        scheme: formData.schemeName || "",
        collectByDate: loanData?.collect_by_date || "",
        remarks: receipt.remarks || "",
      },
      paymentInfo: {
        paymentAmount: totalReceived,
        cashAmount: cash,
        bankAmount: bank,
        paymentMethod,
        chequeNo: receipt.transaction_ref || "",
      },
      // Receipt/settlement money breakdown — the ticket generator renders this
      // (interest paid, discount, insurance, charges, principal, total) in place
      // of the ornaments table for R/S tickets.
      receiptInfo: {
        interestPaid,
        interestDiscount,
        insurancePaid,
        otherCharges: charges,
        principalPaid,
        totalPaid: totalReceived,
        // Outstanding principal BEFORE this receipt ("Op. Balance" on the
        // legacy voucher) and after it.
        balanceBefore: principalBalanceAfter + principalPaid,
        balanceAfter: principalBalanceAfter,
        paymentMethod,
        interestPaidTill: receipt.paid_up_to_date || null,
        // "Against Ref." — the parent loan/pawn voucher this receipt is against.
        loanVoucherNo: receipt.voucher_no || currentVoucher || null,
      },
      ornaments: formattedOrnaments,
      summary: {
        totalOrnamentValue: ornamentValue,
        totalLoanAmount: principalTotal,
        totalInterestAmount: interestPaid,
        totalInsuranceAmount: insurancePaid,
        totalPaymentAmount: totalReceived,
        dueAmount: principalBalanceAfter,
      },
      images: {
        customer: formData.customerPhotoUrl || null,
        ornament: verificationImages.articlePhoto || null,
      },
      meta: {
        ticketType: settled ? "settlement" : "receipt",
        ticketTypeCode,
        pawnMode,
        userId: Number(userId),
        storeId: Number(storeId),
        otherCharges: charges,
        receiptLedgerNo: receipt.voucher_ref || null,
        generatedAt: new Date().toISOString(),
      },
    };
  };

  const openReceiptTicket = (receipt) => {
    if (!receipt) return;
    setPrintDialogOpen(false);
    navigate("/pawn/ticket-generator", {
      state: buildReceiptTicketData(receipt),
    });
  };

  // ─── Derived: most-recent interest payment against this loan ───────────────
  // Sourced from pastReceipts (populated once the settlement backend lands).
  // Until then this stays empty and the fields show a graceful placeholder.
  // Combined paid-on timestamp (date + payment time) for a receipt. Two
  // receipts paid the same day are ordered by the time of day, so the one paid
  // closest to "now" ranks as the most recent. Falls back to id when a row has
  // no time recorded (older data), keeping the order stable.
  const receiptPaidAt = (rec) => {
    const dateStr = rec.date ? dayjs(rec.date).format("YYYY-MM-DD") : null;
    if (!dateStr) return -Infinity;
    const dt = dayjs(`${dateStr}T${rec.time || "00:00:00"}`);
    return dt.isValid() ? dt.valueOf() : dayjs(dateStr).valueOf();
  };

  // Receipts newest-first for display: most recently PAID at the top (by paid
  // date, then time of day, then row id as a final tiebreak). This matches the
  // real-world notion that the latest receipt is the one the customer paid most
  // recently. The backend returns them oldest-first; this reorders + flags the
  // top one "Latest".
  const receiptsNewestFirst = React.useMemo(() => {
    return [...pastReceipts].sort(
      (a, b) =>
        receiptPaidAt(b) - receiptPaidAt(a) ||
        (Number(b.id) || 0) - (Number(a.id) || 0),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastReceipts]);

  const lastPayment = receiptsNewestFirst[0] || null;

  // "20 Jul 2026 · 03:45 PM" (time omitted when the row has none recorded).
  const formatReceiptWhen = (rec) => {
    if (!rec?.date) return "—";
    const datePart = dayjs(rec.date).format("DD MMM YYYY");
    if (!rec.time) return datePart;
    const t = dayjs(`2000-01-01T${rec.time}`);
    return t.isValid() ? `${datePart} · ${t.format("hh:mm A")}` : datePart;
  };

  const lastPaymentLabel = lastPayment
    ? `${dayjs(lastPayment.date).format("DD MMM YYYY")} · ₹${Number(
        lastPayment.amount || 0,
      ).toLocaleString("en-IN")}`
    : "No payments yet";

  // ─── Elapsed-time display (since loan / since last payment) ────────────────
  // Measured against the receipt date. When the store charges month-wise
  // interest (pawn_monthly_interest setting), elapsed time is shown in months
  // passed (with leftover days); otherwise in plain days.
  const monthlyInterestOn = !!pawnSettings?.pawn_monthly_interest;

  // ─── "Interest already paid" state ──────────────────────────────────────────
  // When a prior receipt settled the interest through a date at/after the
  // receipt date, the engine returns zero interest due — nothing should be
  // asked from the customer, so the interest field is disabled (with a banner
  // saying why) and the Interest Discount field is hidden entirely.
  // We can judge "interest already settled" once we have an interest context:
  // a fresh preview (new receipt) OR the loaded receipt's own figures (editing).
  const interestAlreadyPaid =
    (!!interestPreview || !!editingReceipt) &&
    Number(formData.interestAmount) <= 0 &&
    !!formData.interestUpToDate &&
    !dayjs(formData.todayDate).isAfter(dayjs(formData.interestUpToDate), "day");

  // Shortened "interest already paid" note — shown inline with the Payment
  // Calculation heading on desktop, below it on mobile.
  const interestPaidTillLabel = formData.interestUpToDate
    ? `Interest already paid till ${dayjs(formData.interestUpToDate).format(
        "DD MMM YYYY",
      )} — interest and discount fields hidden`
    : "";

  // ── Price breakdown model ──────────────────────────────────────────────────
  // Built from the live preview (the authoritative dues) plus what is currently
  // typed on the screen, so the dialog always explains the figure the teller is
  // looking at — including mid-edit, where the dues can differ from the money on
  // the receipt. null until a loan/preview is loaded, which disables the button.
  const breakdownModel = interestPreview
    ? buildReceiptBreakdown(
        interestPreview,
        {
          total: Number(formData.totalAmountReceived) || 0,
          otherCharges: Number(formData.otherCharges) || 0,
          discount: Number(formData.interestDiscount) || 0,
        },
        {
          voucher: currentVoucher,
          receiptNo: editingReceipt?.receipt_no || null,
          editing: !!editingReceipt,
          customerName: formData.customerName,
          mobile: formData.mobile,
          storeName: currentStoreInfo?.name || "",
          schemeName: formData.schemeName,
          loanAmount: formData.loanAmount,
        },
      )
    : null;

  // ── The two settlement figures ─────────────────────────────────────────────
  // Both null when not settling (getSettlementBaseDue returns null unless so).
  //
  //  settlementPayoff  — what must reach the LOAN to close it (interest +
  //                      insurance + remaining principal − discount). Charges
  //                      are deliberately OUT of it: this is the number that
  //                      stays put while the teller adds a fee on top, so it is
  //                      what the banner quotes and what the Total can't go
  //                      under. Anything typed above it becomes Other Charges.
  //  settlementMinTotal — payoff + the Other Charges currently entered, i.e.
  //                      what the Total field must equal right now. Backs the
  //                      Save guard.
  const settlementBaseDue = getSettlementBaseDue();
  const settlementPayoff =
    settlementBaseDue !== null
      ? Math.max(
          0,
          settlementBaseDue - (Number(formData.interestDiscount) || 0),
        )
      : null;
  const settlementCharges = Number(formData.otherCharges) || 0;
  const settlementMinTotal =
    settlementPayoff !== null ? settlementPayoff + settlementCharges : null;

  // A discount makes no sense with no interest due — clear any stale value so
  // it never reaches the save payload while its field is hidden.
  useEffect(() => {
    if (interestAlreadyPaid && Number(formData.interestDiscount) !== 0) {
      setFormData((prev) => ({ ...prev, interestDiscount: 0 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interestAlreadyPaid]);
  const formatElapsed = (fromDate) => {
    const from = dayjs(fromDate);
    if (!fromDate || !from.isValid()) return "—";
    const ref = dayjs(formData.todayDate).isValid()
      ? dayjs(formData.todayDate)
      : dayjs();
    if (monthlyInterestOn) {
      const months = Math.max(0, ref.diff(from, "month"));
      const remDays = Math.max(0, ref.diff(from.add(months, "month"), "day"));
      const monthPart = `${months} month${months === 1 ? "" : "s"}`;
      return remDays > 0
        ? `${monthPart} ${remDays} day${remDays === 1 ? "" : "s"}`
        : monthPart;
    }
    const days = Math.max(0, ref.diff(from, "day"));
    return `${days} day${days === 1 ? "" : "s"}`;
  };
  const elapsedSinceLoan = formatElapsed(formData.loanDate);
  const elapsedSinceLastPayment = lastPayment
    ? formatElapsed(lastPayment.date)
    : "—";

  // ─── Render ────────────────────────────────────────────────────────────────
  // Green chrome on the settlement screen, blue on the receipt screen. The vars
  // go on :root rather than the wrapper below so the portalled bits (dialogs,
  // select menus, tooltips — all rendered into document.body) are skinned too;
  // GlobalStyles tears them down again when the screen unmounts.
  const accent = settlement ? ACCENT_SETTLEMENT : ACCENT_RECEIPT;
  return (
    <ThemeProvider theme={settlement ? settlementTheme : receiptTheme}>
      <GlobalStyles styles={{ ":root": accentVars(accent) }} />
      <Box
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          bgcolor: BG,
          minHeight: "100%",
        }}
      >
        {/* Header with Search Box. Title + ticket nav + action buttons need
            roughly 1050px side by side, so the row only forms at lg — at md
            they used to squeeze into each other. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { xs: "stretch", lg: "center" },
            gap: 1.5,
            mb: { xs: 2, md: 2.5 },
            pb: 2,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
              minWidth: 0,
              width: { xs: "100%", lg: "auto" },
            }}
          >
            {/* Accent-aware so the pair turns green with the rest of the sheet
                in settlement mode. */}
            <PawnNavButtons accent={ACCENT} accentHover={ACCENT_DARK} />
            <Box
              sx={{
                width: 38,
                height: 38,
                flexShrink: 0,
                borderRadius: "10px",
                bgcolor: ACCENT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px var(--pawn-accent-shadow)",
              }}
            >
              {settlement ? (
                <SettleIcon sx={{ color: "#fff", fontSize: 20 }} />
              ) : (
                <ReceiptIcon sx={{ color: "#fff", fontSize: 20 }} />
              )}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: isMobile ? "1.1rem" : "1.35rem",
                  color: INK,
                  letterSpacing: "-0.02em",
                }}
              >
                {settlement ? "LOAN SETTLEMENT" : "PAWN RECEIPT"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: INK_MUTED, fontFamily: "monospace" }}
              >
                Pawn Ticket Number: {currentVoucher || "—"}
              </Typography>
            </Box>
          </Box>
          {/* Middle: chevron nav + search — centered on desktop, full-width
              row on mobile (sits below the title). */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
              flex: { lg: 1 },
              minWidth: 0,
              width: { xs: "100%", lg: "auto" },
            }}
          >
            {/* Nav: one step back through this screen's own register — from a
                fresh form that lands on the most recent one raised. */}
            <Tooltip
              title={settlement ? "Previous settlement" : "Previous receipt"}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => navigateTicket("prev")}
                  disabled={loadingTicket || searching}
                  sx={navBtnSx}
                >
                  <ChevronLeftIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            <TextField
              size="small"
              placeholder={
                settlement
                  ? "Loan / Settlement / Mobile No"
                  : "Loan / Receipt / Mobile No"
              }
              value={searchVoucher}
              // Same box, two jobs: a voucher / receipt ref submitted with
              // Enter, or a mobile number that quietly drops a customer list
              // under the box (see customerTxnLookup).
              ref={customerLookup.setAnchor}
              onChange={(e) => {
                setSearchVoucher(e.target.value);
                customerLookup.onSearchInput(e.target.value);
              }}
              // onKeyDown, not onKeyPress: the lookup has to see Escape too,
              // and it consumes Enter only on a complete 10-digit mobile
              // number — everything else stays a voucher lookup.
              onKeyDown={(e) => {
                if (customerLookup.handleKeyDown(e)) return;
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearchSubmit(searchVoucher);
                }
              }}
              disabled={loadingTicket}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: INK_MUTED }} />
                  </InputAdornment>
                ),
                endAdornment: (searching ||
                  loadingTicket ||
                  customerLookup.resolving) && (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: { xs: 1, lg: "initial" },
                minWidth: 0,
                width: { xs: "auto", lg: 240 },
                ...fieldSx,
              }}
            />
            {/* Accent-aware, so the dropdown turns green with the rest of the
                sheet in settlement mode. The vars are published on :root, so
                this portalled popper resolves them too. */}
            <CustomerTxnPopper
              {...customerLookup.popperProps}
              accent={ACCENT}
              accentLight={ACCENT_LIGHT}
              accentPale={ACCENT_PALE}
            />
            {/* Nav: one step forward. Only ever shown with one open; stepping
                forward off the newest drops back to a fresh form, which is what
                makes this button disappear again. */}
            {!!editingReceipt && (
              <Tooltip title={settlement ? "Next settlement" : "Next receipt"}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => navigateTicket("next")}
                    disabled={loadingTicket || searching}
                    sx={navBtnSx}
                  >
                    <ChevronRightIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          {/* Right: action buttons — full-width row on mobile, right-aligned
                on desktop. */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              rowGap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: { xs: "stretch", lg: "flex-end" },
              flexShrink: 0,
              width: { xs: "100%", lg: "auto" },
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleSearchSubmit(searchVoucher)}
              disabled={searching}
              startIcon={<SearchIcon />}
              sx={{ flex: { xs: 1, md: "none" } }}
            >
              Load
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ flex: { xs: 1, md: "none" } }}
            >
              Print
            </Button>
            {/* Not drawn at all for a Store Staff session with a past receipt
                open — they may view it, not overwrite it. */}
            {!receiptRoleLocked && (
              <Button
                variant="contained"
                size="small"
                startIcon={
                  saving || sendingSettlementOtp ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : editingReceipt ? (
                    <EditIcon />
                  ) : settlementMode === 1 ? (
                    <CheckCircleIcon />
                  ) : (
                    <SaveIcon />
                  )
                }
                onClick={() => handleSaveReceipt()}
                disabled={
                  saving ||
                  sendingSettlementOtp ||
                  !currentVoucher ||
                  receiptLocked
                }
                // Amber while editing — an unmissable "this OVERWRITES" signal.
                sx={{
                  flex: { xs: 1, md: "none" },
                  ...(editingReceipt
                    ? {
                        bgcolor: WARN,
                        "&:hover": { bgcolor: "#B45309" },
                      }
                    : {}),
                }}
              >
                {receiptLocked
                  ? "Settled"
                  : editingReceipt
                  ? "Update"
                  : settlementMode === 1
                  ? "Settle & Close"
                  : "Save"}
              </Button>
            )}
            {/* Clear the sheet and start a fresh entry, the same way the Payment
                screen's New does — the alternative was stepping forward off the
                newest receipt, which is not an obvious way to ask for a blank
                form. */}
            <Button
              variant="contained"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleNewEntry}
              disabled={saving || sendingSettlementOtp || loadingTicket}
              sx={{
                flex: { xs: 1, md: "none" },
                bgcolor: INK,
                "&:hover": { bgcolor: INK_SOFT },
                boxShadow: "none",
              }}
            >
              New
            </Button>
          </Box>
        </Box>

        {/* ── Settled-loan banner: the loan is closed, so every receipt is
            view-only (no new receipt, no edits). ─────────────────────────── */}
        {receiptLocked && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2.5,
              px: 2,
              py: 1.25,
              borderRadius: "10px",
              bgcolor: SUCCESS_BG,
              border: `1.5px solid ${SUCCESS}`,
              color: SUCCESS,
              fontWeight: 700,
              fontSize: "0.85rem",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 18 }} />
            <Box sx={{ flex: 1 }}>
              This loan is settled — receipts are read-only and can only be
              viewed.
              {editingReceipt &&
                ` Viewing receipt #${editingReceipt.receipt_no}.`}
            </Box>
            {editingReceipt && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={cancelEditReceipt}
                sx={{
                  color: SUCCESS,
                  borderColor: SUCCESS,
                  "&:hover": { borderColor: "#047857", bgcolor: "#D1FAE5" },
                }}
              >
                Close
              </Button>
            )}
          </Box>
        )}

        {/* ── Settlement banner: this screen closes the loan, so say so before
            anything is typed. Once a loan is loaded it also carries the exact
            close-out figure the Total field has been pre-filled with. ─────── */}
        {settlementMode === 1 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 2.5,
              px: 2,
              py: 1.25,
              borderRadius: "10px",
              bgcolor: ACCENT_PALE,
              border: `1.5px solid ${ACCENT}`,
              borderLeftWidth: 5,
            }}
          >
            <SettleIcon sx={{ color: ACCENT, fontSize: 20 }} />
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: ACCENT_DARK }}
              >
                Settlement mode — this receipt closes the loan
              </Typography>
              <Typography variant="caption" sx={{ color: INK_SOFT }}>
                {currentVoucher
                  ? "The whole outstanding balance (interest + insurance + remaining principal) must be collected. Raise the Total to charge more and the extra is booked as Other Charges. The receipt is numbered “H…” and the loan is marked settled — it cannot be re-opened."
                  : "Load a pawn ticket to settle it. For an ordinary part-payment, use the Receipt screen instead."}
              </Typography>
            </Box>
            {settlementPayoff !== null && (
              <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                <Typography
                  variant="caption"
                  sx={{ color: INK_MUTED, display: "block" }}
                >
                  Amount to close
                </Typography>
                <Typography
                  fontWeight={800}
                  sx={{ color: ACCENT_DARK, fontSize: "1.05rem" }}
                >
                  ₹{settlementPayoff.toLocaleString("en-IN")}
                </Typography>
                {/* Charges are shown as their own line rather than folded into
                    the figure above — the payoff is what closes the loan and
                    must stay recognisable while a fee sits on top of it. */}
                {settlementCharges > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: INK_SOFT, display: "block", fontWeight: 600 }}
                  >
                    + ₹{settlementCharges.toLocaleString("en-IN")} other charges
                    {" = "}
                    <Box component="span" sx={{ color: ACCENT_DARK }}>
                      ₹{settlementMinTotal.toLocaleString("en-IN")}
                    </Box>
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* ── Edit-mode banner: assures the teller an EXISTING receipt is
            loaded and Save will overwrite it, with a way out. Hidden on a
            settled loan (that receipt is view-only — see the banner above). ── */}
        {/* ── Staff view-only banner: a past receipt is open, but only the
            Store Head / HOD may change it. ──────────────────────────────── */}
        {receiptRoleLocked && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 2.5,
              px: 2,
              py: 1.25,
              borderRadius: "10px",
              bgcolor: BG,
              border: `1.5px solid ${BORDER}`,
              color: INK_SOFT,
            }}
          >
            <LockIcon sx={{ color: INK_MUTED, fontSize: 20 }} />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="body2" fontWeight={700} sx={{ color: INK }}>
                Viewing receipt #{editingReceipt.receipt_no}
              </Typography>
              <Typography variant="caption" sx={{ color: INK_MUTED }}>
                Read only. Only the Store Head or Store HOD can edit an
                existing {settlement ? "settlement" : "receipt"}.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={cancelEditReceipt}
            >
              Close
            </Button>
          </Box>
        )}

        {editingReceipt && !receiptViewOnly && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 2.5,
              px: 2,
              py: 1.25,
              borderRadius: "10px",
              bgcolor: "#FFFBEB",
              border: `1.5px solid ${WARN}`,
            }}
          >
            <EditIcon sx={{ color: WARN, fontSize: 20 }} />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: "#92400E" }}
              >
                Editing receipt #{editingReceipt.receipt_no}
              </Typography>
              <Typography variant="caption" sx={{ color: "#B45309" }}>
                Update Receipt will overwrite this receipt and rebuild its
                ledger entries. Interest, insurance and principal are
                re-calculated by the server — including any later receipts of
                this loan. Changing the Receipt Date re-prices the dues shown
                here; the amount collected does not change with it.
                {settlement &&
                  " Settlement is paused while an existing receipt is open — cancel the edit to settle the loan."}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={cancelEditReceipt}
              sx={{
                color: "#92400E",
                borderColor: WARN,
                "&:hover": { borderColor: "#92400E", bgcolor: "#FEF3C7" },
              }}
            >
              Cancel Edit
            </Button>
          </Box>
        )}

        {/* Main Grid. Like the Payment screen, the 9/3 split only earns its
            keep from lg up — at md the right-hand rail was a ~225px sliver. */}
        <Grid container spacing={{ xs: 1.5, md: 2.5 }}>
          {/* LEFT COLUMN. View-only (settled loan, or a Store Staff session
              with a past receipt open): nothing on the sheet can be changed.
              The right rail stays live so receipts can still be stepped
              through. */}
          <Grid item xs={12} lg={9} {...leftColumnGuard}>
            {/* Payment Details (RESTRUCTURED) */}
            <SectionCard
              icon="💳"
              title={settlement ? "Settlement Details" : "Payment Details"}
              action={
                interestPreview ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "8px",
                      bgcolor: ACCENT_PALE,
                      border: `1px solid ${ACCENT_LIGHT}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: INK_MUTED, fontWeight: 600 }}
                    >
                      {/* Editing prices the receipt with itself excluded, so the
                          balance shown is the one it started from. */}
                      {editingReceipt
                        ? "Balance before this receipt:"
                        : "Balance:"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.95rem",
                        fontWeight: 800,
                        color: ACCENT_DARK,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      ₹
                      {Number(
                        interestPreview.principal_balance || 0,
                      ).toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                ) : null
              }
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  alignItems: "flex-start",
                  flexDirection: "column",
                }}
              >
                {/* --- Sub-section 1: Payment Calculation (no extra paper) --- */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      mb: 1,
                      mt: 0.5,
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: INK_MUTED, flexShrink: 0 }}
                    >
                      {settlementMode === 1
                        ? "Settlement Calculation"
                        : "Payment Calculation"}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        justifyContent: "flex-end",
                      }}
                    >
                      {/* Interest-already-paid note — inline with the heading on
                          desktop only (on mobile it renders full-width below). */}
                      {interestAlreadyPaid && !receiptLocked && (
                        <Box
                          sx={{
                            display: { xs: "none", md: "flex" },
                            alignItems: "center",
                            gap: 0.75,
                            px: 1,
                            py: 0.4,
                            borderRadius: "8px",
                            bgcolor: SUCCESS_BG,
                            border: `1px solid ${SUCCESS}40`,
                            minWidth: 0,
                          }}
                        >
                          <CheckCircleIcon
                            sx={{ color: SUCCESS, fontSize: 15, flexShrink: 0 }}
                          />
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ color: SUCCESS, fontWeight: 600 }}
                          >
                            {interestPaidTillLabel}
                          </Typography>
                        </Box>
                      )}
                      {/* "Why this much?" — opens the customer-facing price
                          breakdown: the rule that set the months charged, the
                          arithmetic behind each figure, and how the money is
                          applied. Saves the counter from explaining it by hand. */}
                      {breakdownModel && (
                        <Button
                          size="small"
                          onClick={() => setBreakdownOpen(true)}
                          startIcon={<CalculateIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            color: ACCENT_DARK,
                            bgcolor: ACCENT_PALE,
                            border: `1px solid ${ACCENT_LIGHT}`,
                            borderRadius: "8px",
                            px: 1,
                            py: 0.3,
                            flexShrink: 0,
                            "&:hover": { bgcolor: ACCENT_LIGHT },
                          }}
                        >
                          Price Breakup
                        </Button>
                      )}
                      {settlementMode === 1 && !!currentVoucher && (
                        <Chip
                          size="small"
                          icon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
                          label="Settling the loan"
                          sx={{
                            bgcolor: SUCCESS_BG,
                            color: SUCCESS,
                            fontWeight: 700,
                            border: `1px solid ${SUCCESS}55`,
                            "& .MuiChip-icon": { color: SUCCESS },
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Grid container spacing={1.5} width={"100%"}>
                    {/* Interest settled through the receipt date — tell the
                        teller why the interest field is off. Mobile only; on
                        desktop this shows inline with the heading above. Hidden
                        entirely for a settled (read-only) loan. */}
                    {interestAlreadyPaid && !receiptLocked && (
                      <Grid
                        item
                        xs={12}
                        sx={{ display: { xs: "block", md: "none" } }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: "8px",
                            bgcolor: SUCCESS_BG,
                            border: `1px solid ${SUCCESS}40`,
                          }}
                        >
                          <CheckCircleIcon
                            sx={{ color: SUCCESS, fontSize: 18 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: SUCCESS, fontWeight: 600 }}
                          >
                            {interestPaidTillLabel}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {/* Day-wise interest indicator — only in daily mode
                        (pawn_monthly_interest OFF). Shows how many days THIS
                        receipt is charging interest for, distinct from the
                        "days since loan" shown in Loan Details. */}
                    {!monthlyInterestOn &&
                      !interestAlreadyPaid &&
                      interestPreview &&
                      Number(interestPreview.days) > 0 && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.75,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "8px",
                              bgcolor: ACCENT_PALE,
                              border: `1px solid ${ACCENT_LIGHT}`,
                            }}
                          >
                            <span style={{ fontSize: "0.95rem" }}>🗓️</span>
                            <Typography
                              variant="caption"
                              sx={{ color: ACCENT_DARK, fontWeight: 700 }}
                            >
                              Paying interest for{" "}
                              {Number(interestPreview.days).toLocaleString(
                                "en-IN",
                              )}{" "}
                              {Number(interestPreview.days) === 1
                                ? "day"
                                : "days"}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    <Grid item xs={12} sm={6} md={3} lg={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Receipt Date"
                        type="date"
                        value={formData.todayDate}
                        onChange={handleReceiptDateChange}
                        InputLabelProps={{ shrink: true }}
                        sx={fieldSx}
                      />
                    </Grid>
                    {/* "Interest paid till" — settled-through date computed by
                        the engine (grace / half-month / full-month rules). */}
                    <Grid item xs={12} sm={6} md={3} lg={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Interest Paid Till"
                        value={
                          formData.interestUpToDate
                            ? dayjs(formData.interestUpToDate).format(
                                "DD MMM YYYY",
                              )
                            : "—"
                        }
                        InputProps={{ readOnly: true }}
                        sx={readOnlyFieldSx}
                      />
                    </Grid>
                    {/* Interest already settled through the receipt date → no
                        interest is due, so the field is hidden entirely (the
                        green banner above explains why). */}
                    {!interestAlreadyPaid && (
                      <Grid item xs={12} sm={6} md={3} lg={1.6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Interest Amount"
                          type="number"
                          value={formData.interestAmount}
                          InputProps={{
                            readOnly: true,
                            endAdornment: calcInterestLoading && (
                              <InputAdornment position="end">
                                <CircularProgress size={14} />
                              </InputAdornment>
                            ),
                          }}
                          sx={fieldSx}
                        />
                      </Grid>
                    )}
                    {/* Insurance due — engine-computed alongside interest; only
                        shown when the store/loan actually has an insurance rate. */}
                    {formData.insuranceAmount > 0 && (
                      <Grid item xs={12} sm={6} md={3} lg={1.6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Insurance Amount"
                          type="number"
                          value={formData.insuranceAmount}
                          InputProps={{ readOnly: true }}
                          sx={fieldSx}
                        />
                      </Grid>
                    )}
                    {/* No interest due → a discount is meaningless; hide the
                        field entirely and show only Other Charges. */}
                    {!interestAlreadyPaid && (
                      <Grid item xs={12} sm={6} md={3} lg={1.6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Interest Discount"
                          type="number"
                          value={formData.interestDiscount}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              interestDiscount: Math.max(
                                0,
                                parseFloat(e.target.value) || 0,
                              ),
                            }))
                          }
                          inputProps={{ min: 0, step: "any" }}
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "Minus")
                              e.preventDefault();
                          }}
                          sx={fieldSx}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={3} lg={1.6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Other Charges"
                        type="number"
                        value={formData.otherCharges}
                        onChange={(e) => {
                          // Hand-edited ⇒ the whole figure is the teller's own
                          // from here on, so the overflow share stops being
                          // ours to take back (it is inside what they see).
                          autoChargesRef.current = 0;
                          setFormData((prev) => ({
                            ...prev,
                            otherCharges: Math.max(
                              0,
                              parseFloat(e.target.value) || 0,
                            ),
                          }));
                        }}
                        inputProps={{ min: 0, step: "any" }}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "Minus")
                            e.preventDefault();
                        }}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} lg={1.6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Total Amount Received"
                        type="number"
                        value={formData.totalAmountReceived}
                        onChange={handleTotalAmountManualChange}
                        onBlur={handleTotalAmountBlur}
                        inputProps={{
                          // The floor is the charges-free payoff: raising the
                          // Total above it doesn't break the minimum, it just
                          // moves the difference into Other Charges.
                          min: settlementPayoff ?? 0,
                          step: "any",
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "Minus")
                            e.preventDefault();
                        }}
                        // Terse on purpose — the field is one narrow grid cell,
                        // and the settlement banner above carries the full
                        // payoff + charges breakdown.
                        helperText={
                          settlementPayoff !== null
                            ? `Min ₹${settlementPayoff.toLocaleString("en-IN")}`
                            : undefined
                        }
                        FormHelperTextProps={{
                          sx: { color: SUCCESS, fontWeight: 600, mx: 0.25 },
                        }}
                        sx={fieldSx}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* --- Sub-section 2: Payment Method (Paper style) --- */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontWeight: 600,
                      color: INK_MUTED,
                      mb: 1,
                    }}
                  >
                    Payment Method
                  </Typography>

                  <Box
                    sx={{
                      bgcolor: BG_ALT,
                      border: `1px solid ${BORDER}`,
                      borderRadius: "12px",
                      p: 2,
                      boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
                      width: "100%",
                    }}
                  >
                    <Grid container spacing={1.5}>
                      {/* Split Payment Toggle (same feature as Payment screen) */}
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={splitPayment}
                              size="small"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSplitPayment(true);
                                  // Start all-cash; the user re-splits.
                                  setCashAmount(getSplitTotal());
                                  setBankAmount("");
                                  setBankAmount2("");
                                  setSecondBankEnabled(false);
                                  setThirdAccountId("");
                                  setSplitRef1("");
                                  setSplitRef2("");
                                  // Leg 2 defaults to the configured UPI-transfer
                                  // account, else the first non-cash account —
                                  // the dropdown stays freely selectable.
                                  setSecondAccountId(
                                    getUpiAccountId() ||
                                      getFilteredAccounts().find(
                                        (a) =>
                                          a.account_id !== getCashAccountId(),
                                      )?.account_id ||
                                      "",
                                  );
                                } else {
                                  resetSplitState();
                                }
                              }}
                              sx={{
                                color: ACCENT,
                                "&.Mui-checked": { color: ACCENT_DARK },
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ color: INK_SOFT }}
                            >
                              Split Payment (Cash + Bank)
                            </Typography>
                          }
                        />
                      </Grid>

                      {!splitPayment ? (
                        <>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Pay Mode</InputLabel>
                              <Select
                                value={formData.payMode}
                                label="Pay Mode"
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    payMode: e.target.value,
                                  }))
                                }
                                sx={selectSx}
                              >
                                <MenuItem value="CASH">Cash</MenuItem>
                                <MenuItem value="CHEQUE">Cheque</MenuItem>
                                <MenuItem value="CARD">Card Payment</MenuItem>
                                <MenuItem value="UPI">UPI</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Account the money was received into (non-cash legs).
                          Cash is booked to the store's configured cash account
                          automatically by the backend. */}
                          {formData.payMode !== "CASH" && (
                            <Grid item xs={12} md={4}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Received Into Account</InputLabel>
                                <Select
                                  value={selectedBankAccount}
                                  label="Received Into Account"
                                  onChange={(e) =>
                                    setSelectedBankAccount(e.target.value)
                                  }
                                  sx={selectSx}
                                >
                                  {bankAccounts.length === 0 && (
                                    <MenuItem value="" disabled>
                                      No accounts available
                                    </MenuItem>
                                  )}
                                  {bankAccounts.map((acc) => (
                                    <MenuItem
                                      key={acc.account_id}
                                      value={acc.account_id}
                                    >
                                      {acc.account_name ||
                                        acc.name ||
                                        acc.account_id}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}

                          {formData.payMode === "CHEQUE" && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Cheque Number"
                                value={formData.chequeNo}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    chequeNo: e.target.value,
                                  }))
                                }
                                sx={fieldSx}
                              />
                            </Grid>
                          )}

                          {formData.payMode === "CARD" && (
                            <>
                              <Grid item xs={12} sm={4} md={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Card Number"
                                  value={formData.cardNumber}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      cardNumber: e.target.value,
                                    }))
                                  }
                                  sx={fieldSx}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4} md={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Expiry Date"
                                  placeholder="MM/YY"
                                  value={formData.expiryDate}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      expiryDate: e.target.value,
                                    }))
                                  }
                                  sx={fieldSx}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4} md={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Card Holder's Name"
                                  value={formData.cardHolderName}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      cardHolderName: e.target.value,
                                    }))
                                  }
                                  sx={fieldSx}
                                />
                              </Grid>
                            </>
                          )}

                          {formData.payMode === "UPI" && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="UTR Number"
                                value={formData.utrNumber}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    utrNumber: e.target.value,
                                  }))
                                }
                                sx={fieldSx}
                              />
                            </Grid>
                          )}
                        </>
                      ) : (
                        <>
                          {/* ── Cash Payment Card ── */}
                          {/* Cards share the row evenly: 2 visible → half
                              each, 3 visible (2nd transfer added) → thirds. */}
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={secondBankEnabled ? 4 : 6}
                          >
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                borderRadius: "10px",
                                borderColor: SUCCESS,
                                borderWidth: "1.5px",
                                bgcolor: `${SUCCESS}06`,
                                height: "100%",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  mb: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 3,
                                    height: 16,
                                    bgcolor: SUCCESS,
                                    borderRadius: 2,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  sx={{
                                    color: SUCCESS,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  💵 Cash Payment
                                </Typography>
                              </Box>

                              {/* Cash is booked to the store's configured cash
                                  account — shown read-only for clarity. */}
                              <TextField
                                fullWidth
                                size="small"
                                label="Cash Account"
                                value={
                                  bankAccounts.find(
                                    (a) => a.account_id === getCashAccountId(),
                                  )?.account_name || "Cash Account"
                                }
                                InputProps={{ readOnly: true }}
                                sx={{
                                  ...fieldSx,
                                  mb: 1,
                                  "& .MuiInputBase-input": {
                                    color: INK_SOFT,
                                    fontSize: "0.82rem",
                                  },
                                }}
                              />

                              <TextField
                                fullWidth
                                size="small"
                                label="Cash Amount"
                                type="number"
                                value={cashAmount}
                                onChange={(e) => {
                                  if (secondBankEnabled)
                                    handleCash3(e.target.value);
                                  else handleCashAmountChange(e.target.value);
                                }}
                                sx={{
                                  ...fieldSx,
                                  "& .MuiInputBase-input": {
                                    fontWeight: 700,
                                    color: SUCCESS,
                                  },
                                }}
                                inputProps={{ min: 0, step: "any" }}
                                onKeyDown={(e) => {
                                  if (e.key === "-" || e.key === "Minus")
                                    e.preventDefault();
                                }}
                              />
                            </Paper>
                          </Grid>

                          {/* ── Account Transfer 1 Card ── */}
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={secondBankEnabled ? 4 : 6}
                          >
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                borderRadius: "10px",
                                borderColor: INFO_C,
                                borderWidth: "1.5px",
                                bgcolor: `${INFO_C}06`,
                                height: "100%",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  mb: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 3,
                                    height: 16,
                                    bgcolor: INFO_C,
                                    borderRadius: 2,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  sx={{
                                    color: INFO_C,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  🏦 Account Transfer 1
                                </Typography>
                              </Box>

                              {/* Defaults to the configured UPI-transfer
                                  account but freely selectable */}
                              <FormControl
                                fullWidth
                                size="small"
                                sx={{ mb: 1 }}
                              >
                                <InputLabel>Bank Account</InputLabel>
                                <Select
                                  value={secondAccountId}
                                  label="Bank Account"
                                  onChange={(e) =>
                                    setSecondAccountId(e.target.value)
                                  }
                                  sx={selectSx}
                                >
                                  {getFilteredAccounts()
                                    .filter(
                                      (a) =>
                                        a.account_id !== getCashAccountId() &&
                                        a.account_id !== thirdAccountId,
                                    )
                                    .map((acc) => (
                                      <MenuItem
                                        key={acc.account_id}
                                        value={acc.account_id}
                                      >
                                        🏦{" "}
                                        {acc.account_name ||
                                          acc.name ||
                                          acc.account_id}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>

                              <TextField
                                fullWidth
                                size="small"
                                label="Bank Amount"
                                type="number"
                                value={bankAmount}
                                onChange={(e) => {
                                  if (secondBankEnabled)
                                    handleBank1_3(e.target.value);
                                  else handleBankAmountChange(e.target.value);
                                }}
                                sx={{
                                  ...fieldSx,
                                  "& .MuiInputBase-input": {
                                    fontWeight: 700,
                                    color: INFO_C,
                                  },
                                }}
                                inputProps={{ min: 0, step: "any" }}
                                onKeyDown={(e) => {
                                  if (e.key === "-" || e.key === "Minus")
                                    e.preventDefault();
                                }}
                              />

                              <TextField
                                fullWidth
                                size="small"
                                label="Transaction Reference Number"
                                placeholder="Cheque no / transaction ref"
                                value={splitRef1}
                                onChange={(e) => setSplitRef1(e.target.value)}
                                sx={{ ...fieldSx, mt: 1 }}
                              />

                              {!secondBankEnabled && (
                                <Button
                                  size="small"
                                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                  onClick={handleAddSecondBank}
                                  sx={{
                                    mt: 1,
                                    color: INFO_C,
                                    fontSize: "0.72rem",
                                    fontWeight: 700,
                                    textTransform: "none",
                                    p: "2px 6px",
                                    "&:hover": { bgcolor: `${INFO_C}10` },
                                  }}
                                >
                                  Add Another
                                </Button>
                              )}
                            </Paper>
                          </Grid>

                          {/* ── Account Transfer 2 Card (optional) ── */}
                          {secondBankEnabled && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  borderRadius: "10px",
                                  borderColor: INFO_C,
                                  borderWidth: "1.5px",
                                  bgcolor: `${INFO_C}06`,
                                  height: "100%",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 0.75,
                                    mb: 1.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.75,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 3,
                                        height: 16,
                                        bgcolor: INFO_C,
                                        borderRadius: 2,
                                      }}
                                    />
                                    <Typography
                                      variant="caption"
                                      fontWeight={700}
                                      sx={{
                                        color: INFO_C,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.06em",
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      🏦 Account Transfer 2
                                    </Typography>
                                  </Box>
                                  <Tooltip title="Remove this transfer">
                                    <IconButton
                                      size="small"
                                      onClick={handleRemoveSecondBank}
                                      sx={{ p: 0.25, color: INK_MUTED }}
                                    >
                                      <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>

                                <FormControl
                                  fullWidth
                                  size="small"
                                  sx={{ mb: 1 }}
                                >
                                  <InputLabel>Bank Account</InputLabel>
                                  <Select
                                    value={thirdAccountId}
                                    label="Bank Account"
                                    onChange={(e) =>
                                      setThirdAccountId(e.target.value)
                                    }
                                    sx={selectSx}
                                  >
                                    {getFilteredAccounts()
                                      .filter(
                                        (a) =>
                                          a.account_id !== getCashAccountId() &&
                                          a.account_id !== secondAccountId,
                                      )
                                      .map((acc) => (
                                        <MenuItem
                                          key={acc.account_id}
                                          value={acc.account_id}
                                        >
                                          🏦{" "}
                                          {acc.account_name ||
                                            acc.name ||
                                            acc.account_id}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>

                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Bank Amount"
                                  type="number"
                                  value={bankAmount2}
                                  onChange={(e) =>
                                    handleBank2_3(e.target.value)
                                  }
                                  sx={{
                                    ...fieldSx,
                                    "& .MuiInputBase-input": {
                                      fontWeight: 700,
                                      color: INFO_C,
                                    },
                                  }}
                                  inputProps={{ min: 0, step: "any" }}
                                  onKeyDown={(e) => {
                                    if (e.key === "-" || e.key === "Minus")
                                      e.preventDefault();
                                  }}
                                />

                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Transaction Reference Number"
                                  placeholder="Cheque no / transaction ref"
                                  value={splitRef2}
                                  onChange={(e) => setSplitRef2(e.target.value)}
                                  sx={{ ...fieldSx, mt: 1 }}
                                />
                              </Paper>
                            </Grid>
                          )}

                          {/* ── Legs total vs Total Amount Received ── */}
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ color: INK, fontSize: "0.82rem" }}
                              >
                                Total: ₹
                                {(
                                  Number(cashAmount || 0) +
                                  Number(bankAmount || 0) +
                                  Number(bankAmount2 || 0)
                                ).toLocaleString("en-IN")}
                              </Typography>
                              {getSplitTotal() > 0 &&
                                Number(cashAmount || 0) +
                                  Number(bankAmount || 0) +
                                  Number(bankAmount2 || 0) !==
                                  getSplitTotal() && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: ERROR_C,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.25,
                                    }}
                                  >
                                    ⚠ Expected ₹
                                    {getSplitTotal().toLocaleString("en-IN")}
                                  </Typography>
                                )}
                            </Box>
                          </Grid>
                        </>
                      )}

                      {/* Remarks */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Remarks"
                          multiline
                          rows={2}
                          value={formData.remarks}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              remarks: e.target.value,
                            }))
                          }
                          sx={fieldSx}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
            </SectionCard>

            {/* Customer Details & Loan Details */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "stretch",
                gap: 2,
              }}
            >
              {/* Customer Details */}
              <Box sx={{ flex: { sm: "1 1 50%" }, minWidth: 0 }}>
                <SectionCard icon="👤" title="Customer Details">
                  {(() => {
                    const hasPhoto = !!formData.customerPhotoUrl;
                    const hasId = !!formData.idProofPhotoUrl;
                    const hasImages = hasPhoto || hasId;
                    // Uniform framed boxes so mismatched source dimensions never
                    // render at different sizes: customer photo is square, ID is
                    // a landscape (card-shaped) rectangle. Images fill via cover.
                    const frameSx = (w, h) => ({
                      width: w,
                      height: h,
                      borderRadius: 1.5,
                      border: `1px solid ${BORDER}`,
                      overflow: "hidden",
                      bgcolor: BG_ALT,
                    });
                    const imgSx = {
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    };
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: 2,
                          alignItems: "stretch",
                        }}
                      >
                        {/* LEFT: details stacked vertically */}
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            label="Name"
                            value={formData.customerName}
                            InputProps={{ readOnly: true }}
                            sx={readOnlyFieldSx}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Mobile"
                            value={formData.mobile}
                            InputProps={{ readOnly: true }}
                            sx={readOnlyFieldSx}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Address"
                            value={formData.address}
                            multiline
                            rows={2}
                            InputProps={{ readOnly: true }}
                            sx={readOnlyFieldSx}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="S/W/D of"
                            value={
                              formData.relativeRelation
                                ? `${formData.relativeRelation} ${formData.relativeName}`
                                : formData.relativeName
                            }
                            InputProps={{ readOnly: true }}
                            sx={readOnlyFieldSx}
                          />
                        </Box>

                        {/* RIGHT: photo (square) on top, ID (rectangle) below.
                            When only one image exists it centers vertically. The
                            whole column is omitted when there are no images, so
                            the details fill the full width. */}
                        {hasImages && (
                          <Box
                            sx={{
                              flexShrink: 0,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent:
                                hasPhoto && hasId ? "flex-start" : "center",
                              gap: 1.5,
                              width: { xs: "100%", sm: "auto" },
                            }}
                          >
                            {hasPhoto && (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 0.4,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: INK_MUTED }}
                                >
                                  Customer Photo
                                </Typography>
                                <Box sx={frameSx(100, 100)}>
                                  <Box
                                    component="img"
                                    src={formData.customerPhotoUrl}
                                    alt="Customer"
                                    sx={imgSx}
                                  />
                                </Box>
                              </Box>
                            )}
                            {hasId && (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 0.4,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: INK_MUTED }}
                                >
                                  ID Proof
                                </Typography>
                                <Box sx={frameSx(160, 100)}>
                                  <Box
                                    component="img"
                                    src={formData.idProofPhotoUrl}
                                    alt="ID proof"
                                    sx={imgSx}
                                  />
                                </Box>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </SectionCard>
              </Box>

              {/* Loan Details */}
              <Box sx={{ flex: { sm: "1 1 50%" }, minWidth: 0 }}>
                <SectionCard icon="💰" title="Loan Details">
                  <Grid container spacing={1.5}>
                    {/* Date */}
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={0.5}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: INK_MUTED,
                            ml: 0.5,
                          }}
                        >
                          Loan Date
                        </Typography>

                        <TextField
                          fullWidth
                          size="small"
                          value={formData.loanDate}
                          InputProps={{ readOnly: true }}
                          sx={readOnlyFieldSx}
                        />
                      </Stack>
                    </Grid>

                    {/* Amount */}
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={0.5}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: INK_MUTED,
                            ml: 0.5,
                          }}
                        >
                          Loan Amount
                        </Typography>

                        <TextField
                          fullWidth
                          size="small"
                          value={formData.loanAmount}
                          InputProps={{ readOnly: true }}
                          sx={readOnlyFieldSx}
                        />
                      </Stack>
                    </Grid>

                    {/* Days Since Loan */}
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={0.5}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: INK_MUTED,
                            ml: 0.5,
                          }}
                        >
                          {monthlyInterestOn
                            ? "Months Since Loan"
                            : "Days Since Loan"}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={elapsedSinceLoan}
                          InputProps={{ readOnly: true }}
                          sx={readOnlyFieldSx}
                        />
                      </Stack>
                    </Grid>

                    {/* Interest (%) — the label doubles as the touch reveal
                        target (see useSecretRevealTap): on handhelds a double
                        tap here flips an above-cap loan between the 1.16% cap
                        and its true rate, standing in for F2+O. */}
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={0.5}>
                        <Typography
                          variant="caption"
                          {...revealTap}
                          sx={{
                            fontWeight: 600,
                            color: INK_MUTED,
                            ml: 0.5,
                            ...SECRET_TAP_SX,
                          }}
                        >
                          Interest (%)
                        </Typography>

                        <TextField
                          fullWidth
                          size="small"
                          value={displayInterestRate}
                          InputProps={{
                            readOnly: true,
                            // Faint secret mark: barely-visible while masked,
                            // turns solid once the true rate is revealed.
                            endAdornment: showSecretMark ? (
                              <InputAdornment position="end">
                                <Box
                                  sx={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: "50%",
                                    bgcolor: revealInterest
                                      ? ERROR_C
                                      : "rgba(220,38,38,0.16)",
                                    transition: "background-color 0.2s ease",
                                  }}
                                />
                              </InputAdornment>
                            ) : undefined,
                          }}
                          sx={readOnlyFieldSx}
                        />
                      </Stack>
                    </Grid>

                    {/* Last Payment Received */}
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={0.5}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: INK_MUTED,
                            ml: 0.5,
                          }}
                        >
                          Last Payment Received
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={lastPaymentLabel}
                          InputProps={{ readOnly: true }}
                          sx={readOnlyFieldSx}
                        />
                      </Stack>
                    </Grid>

                    {/* Days Since Last Payment */}
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={0.5}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: INK_MUTED,
                            ml: 0.5,
                          }}
                        >
                          {monthlyInterestOn
                            ? "Months Since Last Payment"
                            : "Days Since Last Payment"}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={elapsedSinceLastPayment}
                          InputProps={{ readOnly: true }}
                          sx={readOnlyFieldSx}
                        />
                      </Stack>
                    </Grid>

                    {/* Scheme */}
                    {formData.schemeName && (
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={0.5}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: INK_MUTED,
                              ml: 0.5,
                            }}
                          >
                            Scheme
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            value={formData.schemeName}
                            InputProps={{ readOnly: true }}
                            sx={readOnlyFieldSx}
                          />
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </SectionCard>
              </Box>
            </Box>

            {/* Ornaments Details & Verification (side by side) */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "stretch",
                gap: 2,
              }}
            >
              {/* 4. Ornaments Details */}
              <Box sx={{ flex: { sm: "1 1 50%" }, minWidth: 0 }}>
                <SectionCard icon="💎" title="Ornaments Details">
                  {ornaments.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ py: 2, textAlign: "center" }}
                    >
                      No ornaments found for this loan.
                    </Typography>
                  ) : (
                    <TableContainer
                      component={Paper}
                      sx={{
                        boxShadow: "none",
                        border: `1px solid ${BORDER}`,
                        borderRadius: 2,
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: BG_ALT }}>
                            <TableCell>Sl No</TableCell>
                            <TableCell>Item Name</TableCell>
                            <TableCell align="right">Pcs</TableCell>
                            <TableCell align="right">Gross(g)</TableCell>
                            <TableCell align="right">Value(₹)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ornaments.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>{item.item_name}</TableCell>
                              <TableCell align="right">
                                {Number(item.item_pcs) || 1}
                              </TableCell>
                              <TableCell align="right">
                                {parseFloat(item.item_gross).toFixed(3)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontWeight: 700, color: ACCENT }}
                              >
                                ₹
                                {Number(item.item_value).toLocaleString(
                                  "en-IN",
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </SectionCard>
              </Box>

              {/* 5. Verification Section */}
              <Box sx={{ flex: { sm: "1 1 50%" }, minWidth: 0 }}>
                <SectionCard icon="🔐" title="Verification">
                  <Grid container spacing={2}>
                    {["fingerprint1", "fingerprint2", "articlePhoto"].map(
                      (imgKey) => (
                        <Grid item xs={12} sm={4} key={imgKey}>
                          <Typography
                            variant="caption"
                            sx={{ color: INK_MUTED, mb: 0.5, display: "block" }}
                          >
                            {imgKey === "fingerprint1"
                              ? "Finger Print -1"
                              : imgKey === "fingerprint2"
                              ? "Finger Print -2"
                              : "Article Photo"}
                          </Typography>
                          <Box
                            onClick={() =>
                              setImageSourceDialog({
                                open: true,
                                index: imgKey,
                              })
                            }
                            sx={{
                              width: "100%",
                              height: 100,
                              borderRadius: 2,
                              border: `2px dashed ${BORDER_MED}`,
                              bgcolor: BG,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              "&:hover": {
                                borderColor: ACCENT,
                                bgcolor: ACCENT_PALE,
                              },
                            }}
                          >
                            {verificationImages[imgKey] ? (
                              <Box
                                component="img"
                                src={verificationImages[imgKey]}
                                sx={{ height: "100%", objectFit: "contain" }}
                              />
                            ) : imgKey.includes("fingerprint") ? (
                              <FingerprintIcon
                                sx={{ fontSize: 40, color: BORDER_MED }}
                              />
                            ) : (
                              <PhotoIcon
                                sx={{ fontSize: 40, color: BORDER_MED }}
                              />
                            )}
                          </Box>
                        </Grid>
                      ),
                    )}
                  </Grid>
                </SectionCard>
              </Box>
            </Box>
          </Grid>

          {/* RIGHT COLUMN — visual-only summary; kept narrow and compact like
              the Payment screen so the form panel gets the space. Below lg it
              becomes a full-width block under the form, so the sticky offset
              (which only makes sense beside the form) comes off with it. */}
          <Grid item xs={12} lg={3}>
            <Card
              sx={{
                mb: 2,
                position: { xs: "static", lg: "sticky" },
                top: { lg: 16 },
              }}
            >
              <Box
                sx={{
                  height: 4,
                  background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_MED})`,
                }}
              />
              <CardContent sx={{ p: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.25,
                  }}
                >
                  {settlement ? (
                    <SettleIcon sx={{ color: ACCENT, fontSize: 18 }} />
                  ) : (
                    <ReceiptIcon sx={{ color: ACCENT, fontSize: 18 }} />
                  )}
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ color: INK }}
                  >
                    {settlement ? "Settlement Preview" : "Receipt Ticket Preview"}
                  </Typography>
                </Box>
                {/* Compact 2-per-row layout: ticket + customer, loan below */}
                <Grid container spacing={0.75}>
                  {[
                    { label: "Pawn Ticket No.", value: currentVoucher || "—" },
                    {
                      label: "Customer",
                      value: formData.customerName || "—",
                      bold: true,
                    },
                    {
                      label: "Loan Amount",
                      value: `₹${formData.loanAmount.toLocaleString("en-IN")}`,
                      bold: true,
                      color: ACCENT,
                    },
                  ].map(({ label, value, bold, color }) => (
                    <Grid item xs={6} key={label}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: INK_MUTED,
                          fontSize: "0.65rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={bold ? 700 : 500}
                        sx={{
                          color: color || INK,
                          fontSize: "0.8rem",
                          lineHeight: 1.3,
                          wordBreak: "break-word",
                        }}
                      >
                        {value}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                {/* ── Receipt Summary — the money being collected today ──── */}
                <Box
                  sx={{
                    mt: 1.25,
                    p: 1.25,
                    borderRadius: "10px",
                    bgcolor: ACCENT_PALE,
                    border: `1px solid ${ACCENT_LIGHT}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontWeight: 700,
                      color: ACCENT_DARK,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      mb: 1,
                    }}
                  >
                    {settlement ? "Settlement Summary" : "Receipt Summary"}
                  </Typography>
                  <Stack spacing={0.75}>
                    {/* Same price breakdown as the Payment Calculation header —
                        repeated here because this rail is what gets turned
                        towards the customer while the amount is discussed. */}
                    {breakdownModel && (
                      <Button
                        size="small"
                        fullWidth
                        onClick={() => setBreakdownOpen(true)}
                        startIcon={<CalculateIcon sx={{ fontSize: 15 }} />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          color: ACCENT_DARK,
                          justifyContent: "flex-start",
                          bgcolor: "#fff",
                          border: `1px solid ${ACCENT_LIGHT}`,
                          borderRadius: "8px",
                          py: 0.3,
                          mb: 0.25,
                          "&:hover": { bgcolor: ACCENT_LIGHT },
                        }}
                      >
                        Show price breakup to customer
                      </Button>
                    )}
                    {[
                      {
                        // What the amount entered ACTUALLY covers, not the dues:
                        // interest is served first, so an under-payment must not
                        // read as though the insurance was collected.
                        label: "Interest Received",
                        value: `₹${(breakdownModel
                          ? breakdownModel.applied.interest
                          : formData.interestAmount
                        ).toLocaleString("en-IN")}`,
                      },
                      ...(formData.insuranceAmount > 0
                        ? [
                            {
                              label: "Insurance Received",
                              value: `₹${(breakdownModel
                                ? breakdownModel.applied.insurance
                                : formData.insuranceAmount
                              ).toLocaleString("en-IN")}`,
                            },
                          ]
                        : []),
                      ...(breakdownModel && breakdownModel.applied.principal > 0
                        ? [
                            {
                              label: "Towards Principal",
                              value: `₹${breakdownModel.applied.principal.toLocaleString(
                                "en-IN",
                              )}`,
                            },
                          ]
                        : []),
                      ...(breakdownModel && breakdownModel.outcome.shortfall > 0
                        ? [
                            {
                              label: "Unpaid — carries forward",
                              value: `₹${breakdownModel.outcome.shortfall.toLocaleString(
                                "en-IN",
                              )}`,
                            },
                          ]
                        : []),
                      ...(interestPreview
                        ? [
                            {
                              label: "Principal Balance",
                              value: `₹${Number(
                                interestPreview.principal_balance || 0,
                              ).toLocaleString("en-IN")}`,
                            },
                          ]
                        : []),
                    ].map(({ label, value }) => (
                      <Box
                        key={label}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" sx={{ color: INK_MUTED }}>
                          {label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: INK_SOFT,
                            fontFamily: "monospace",
                          }}
                        >
                          {value}
                        </Typography>
                      </Box>
                    ))}
                    {formData.interestUpToDate && (
                      <>
                        <Divider sx={{ my: 0.5, borderColor: ACCENT_LIGHT }} />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: INK_MUTED }}
                          >
                            Interest Paid Till
                          </Typography>
                          <Chip
                            size="small"
                            label={dayjs(formData.interestUpToDate).format(
                              "DD MMM YYYY",
                            )}
                            sx={{
                              bgcolor: SUCCESS_BG,
                              color: SUCCESS,
                              fontWeight: 700,
                            }}
                          />
                        </Box>
                      </>
                    )}
                  </Stack>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <Box sx={{ height: 3, bgcolor: BG_ALT }} />
              <CardContent sx={{ p: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.25,
                  }}
                >
                  <HistoryIcon sx={{ color: INK_MUTED, fontSize: 17 }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ color: INK }}
                  >
                    Past Receipts
                  </Typography>
                  {pastReceipts.length > 0 && (
                    <Chip
                      label={pastReceipts.length}
                      size="small"
                      sx={{
                        ml: "auto",
                        height: 18,
                        fontSize: "0.65rem",
                        bgcolor: ACCENT_LIGHT,
                        color: ACCENT_DARK,
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
                {pastReceipts.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ textAlign: "center", py: 1.5, fontSize: "0.8rem" }}
                  >
                    No previous receipts found.
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="caption"
                      sx={{
                        color: INK_MUTED,
                        display: "block",
                        mb: 0.75,
                        fontSize: "0.68rem",
                      }}
                    >
                      {canEditRecords
                        ? "Click a receipt to load and edit it."
                        : "Click a receipt to view it."}
                    </Typography>
                    <Stack spacing={0.75}>
                      {receiptsNewestFirst.map((rec, idx) => {
                        const isEditingThis = editingReceipt?.id === rec.id;
                        const isLatest = idx === 0;
                        return (
                          <Tooltip
                            key={rec.id ?? idx}
                            placement="left"
                            title={
                              receiptLocked || !canEditRecords
                                ? "Click to view this receipt (read-only)"
                                : "Click to edit this receipt"
                            }
                          >
                            <Box
                              onClick={() => startEditReceipt(rec)}
                              sx={{
                                p: 1,
                                borderRadius: "8px",
                                border: `1.5px solid ${
                                  isEditingThis ? WARN : BORDER
                                }`,
                                bgcolor: isEditingThis
                                  ? "#FFFBEB"
                                  : isLatest
                                  ? ACCENT_SUBTLE
                                  : BG,
                                cursor: "pointer",
                                transition:
                                  "border-color 0.15s ease, background-color 0.15s ease",
                                "&:hover": {
                                  borderColor: isEditingThis ? WARN : ACCENT,
                                  bgcolor: isEditingThis
                                    ? "#FFFBEB"
                                    : ACCENT_PALE,
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    fontWeight={700}
                                    sx={{ color: isLatest ? ACCENT : INK_SOFT }}
                                  >
                                    #{rec.receipt_no}
                                  </Typography>
                                  {loadingReceiptId === rec.id ? (
                                    <CircularProgress size={12} />
                                  ) : isEditingThis ? (
                                    <Chip
                                      size="small"
                                      icon={<EditIcon sx={{ fontSize: 11 }} />}
                                      label={
                                        receiptLocked || !canEditRecords
                                          ? "Viewing"
                                          : "Editing"
                                      }
                                      sx={{
                                        height: 16,
                                        fontSize: "0.6rem",
                                        bgcolor: "#FEF3C7",
                                        color: "#92400E",
                                        "& .MuiChip-icon": { color: WARN },
                                      }}
                                    />
                                  ) : (
                                    isLatest && (
                                      <Chip
                                        size="small"
                                        label="Latest"
                                        sx={{
                                          height: 16,
                                          fontSize: "0.6rem",
                                          bgcolor: ACCENT_LIGHT,
                                          color: ACCENT_DARK,
                                          fontWeight: 700,
                                        }}
                                      />
                                    )
                                  )}
                                </Box>
                                {/* Paid-on date + time (12h). Two receipts on
                                    the same day are ordered by this time. */}
                                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: INK_MUTED,
                                      display: "block",
                                      lineHeight: 1.25,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {dayjs(rec.date).format("DD MMM YYYY")}
                                  </Typography>
                                  {rec.time &&
                                    dayjs(
                                      `2000-01-01T${rec.time}`,
                                    ).isValid() && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: INK_MUTED,
                                          display: "block",
                                          fontSize: "0.72rem",
                                          lineHeight: 1.5,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {dayjs(`2000-01-01T${rec.time}`).format(
                                          "hh:mm A",
                                        )}
                                      </Typography>
                                    )}
                                </Box>
                              </Box>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ fontSize: "0.85rem" }}
                              >
                                ₹{rec.amount.toLocaleString("en-IN")}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: INK_MUTED,
                                  display: "block",
                                  fontSize: "0.68rem",
                                }}
                              >
                                Int ₹
                                {rec.interest_deducted.toLocaleString("en-IN")}
                                {rec.insurance_deducted > 0
                                  ? ` · Ins ₹${rec.insurance_deducted.toLocaleString(
                                      "en-IN",
                                    )}`
                                  : ""}
                                {` · Prin ₹${rec.principal_reduction.toLocaleString(
                                  "en-IN",
                                )}`}
                              </Typography>
                              {rec.paid_up_to_date && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: SUCCESS,
                                    display: "block",
                                    fontWeight: 600,
                                    fontSize: "0.68rem",
                                  }}
                                >
                                  Paid till{" "}
                                  {dayjs(rec.paid_up_to_date).format(
                                    "DD MMM YYYY",
                                  )}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
              <Box sx={{ height: 3, bgcolor: BG_ALT }} />
              <CardContent sx={{ p: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.25,
                  }}
                >
                  <AccountBalanceIcon sx={{ color: INK_MUTED, fontSize: 17 }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ color: INK }}
                  >
                    Past Loans
                  </Typography>
                  {previousLoans.length > 0 && (
                    <Chip
                      label={previousLoans.length}
                      size="small"
                      sx={{
                        ml: "auto",
                        height: 18,
                        fontSize: "0.65rem",
                        bgcolor: ACCENT_LIGHT,
                        color: ACCENT_DARK,
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
                {previousLoans.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ textAlign: "center", py: 1.5, fontSize: "0.8rem" }}
                  >
                    No previous loans found.
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {previousLoans.map((loan, idx) => {
                      const isCurrent =
                        String(loan.voucher_no) === String(currentVoucher);
                      const isLoadingThis =
                        String(loadingPrevVoucher) === String(loan.voucher_no);
                      const anyLoading =
                        searching || loadingTicket || !!loadingPrevVoucher;
                      return (
                        <Box
                          key={loan.id ?? idx}
                          onClick={() =>
                            !isCurrent && handleSelectPreviousLoan(loan)
                          }
                          role="button"
                          tabIndex={isCurrent ? -1 : 0}
                          onKeyDown={(e) => {
                            if (
                              !isCurrent &&
                              (e.key === "Enter" || e.key === " ")
                            ) {
                              e.preventDefault();
                              handleSelectPreviousLoan(loan);
                            }
                          }}
                          sx={{
                            p: 1,
                            borderRadius: "8px",
                            border: `1.5px solid ${isCurrent ? ACCENT : BORDER}`,
                            bgcolor: isCurrent ? ACCENT_SUBTLE : BG,
                            cursor: isCurrent
                              ? "default"
                              : anyLoading
                              ? "wait"
                              : "pointer",
                            outline: "none",
                            transition:
                              "border-color 0.15s ease, background-color 0.15s ease",
                            "&:hover": {
                              borderColor: isCurrent ? ACCENT : ACCENT_MED,
                              bgcolor: isCurrent ? ACCENT_SUBTLE : ACCENT_PALE,
                            },
                            "&:focus-visible": {
                              borderColor: ACCENT,
                              boxShadow: `0 0 0 3px ${ACCENT_LIGHT}`,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                sx={{
                                  color: INK_SOFT,
                                  fontFamily: "monospace",
                                }}
                              >
                                #{loan.voucher_no}
                              </Typography>
                              {isLoadingThis && <CircularProgress size={12} />}
                            </Box>
                            <Chip
                              size="small"
                              label={
                                isCurrent
                                  ? "Loaded"
                                  : loan.is_settled === "Y"
                                  ? "Closed"
                                  : "Active"
                              }
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                bgcolor: isCurrent
                                  ? ACCENT
                                  : loan.is_settled === "Y"
                                  ? SUCCESS_BG
                                  : ACCENT_LIGHT,
                                color: isCurrent
                                  ? "#fff"
                                  : loan.is_settled === "Y"
                                  ? SUCCESS
                                  : ACCENT_DARK,
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "baseline",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ color: ACCENT, fontSize: "0.85rem" }}
                            >
                              ₹
                              {Number(loan.loan_amount || 0).toLocaleString(
                                "en-IN",
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: INK_MUTED, fontSize: "0.68rem" }}
                            >
                              {dayjs(loan.transaction_date).format(
                                "DD MMM YYYY",
                              )}
                              {loan.loan_interest
                                ? ` · ${maskRate(loan.loan_interest)}%`
                                : ""}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Hidden file inputs for images */}
        {["fingerprint1", "fingerprint2", "articlePhoto"].map((name) => (
          <React.Fragment key={name}>
            <input
              id={`receipt-camera-input-${name}`}
              type="file"
              hidden
              accept="image/*"
              capture="environment"
            />
            <input
              id={`receipt-gallery-input-${name}`}
              type="file"
              hidden
              accept="image/*"
            />
          </React.Fragment>
        ))}
        <ImageSourceDialog
          open={imageSourceDialog.open}
          index={imageSourceDialog.index}
          prefix="receipt-"
          onClose={() => setImageSourceDialog({ open: false, index: null })}
        />

        {/* Price breakdown — how the amount was arrived at, in the customer's
            language, printable to hand over. */}
        <ReceiptBreakdownDialog
          open={breakdownOpen}
          onClose={() => setBreakdownOpen(false)}
          model={breakdownModel}
        />

        {/* ── Under-payment confirmation ────────────────────────────────────────
            Saving less than the dues is allowed (partial payments are a real
            counter workflow) but it must never happen unnoticed: interest is
            served first, so the tail — usually insurance — silently carries to
            the next receipt. This spells out the exact split the engine will
            book and makes the teller own the decision. It matters most on an
            edit, where re-dating a receipt can raise the dues while the money
            on it stays unchanged. */}
        {/* ── "New" confirmation: the sheet holds a loaded loan or typed
            figures, so say what is about to be dropped before dropping it.
            Nothing is saved or deleted either way — this only clears the
            screen. ──────────────────────────────────────────────────────── */}
        <Dialog
          open={newEntryConfirm}
          onClose={() => setNewEntryConfirm(false)}
          PaperProps={{ sx: { borderRadius: 3, width: "100%", maxWidth: 400 } }}
        >
          <DialogTitle sx={{ fontSize: "1rem", fontWeight: 800, pb: 1 }}>
            {settlement ? "Start a new settlement?" : "Start a new receipt?"}
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Typography sx={{ fontSize: "0.85rem", color: INK_SOFT }}>
              The screen will be cleared and ready for a fresh entry. Nothing
              already saved is affected — only what is on screen now is
              discarded.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 0.5, gap: 1 }}>
            <Button
              onClick={() => setNewEntryConfirm(false)}
              sx={{ textTransform: "none", fontWeight: 700, color: INK_MUTED }}
            >
              Keep working
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setNewEntryConfirm(false);
                clearScreenForNewEntry();
              }}
              sx={{ textTransform: "none", fontWeight: 700, minWidth: 110 }}
            >
              Start New
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!shortfallPrompt}
          onClose={() => setShortfallPrompt(null)}
          PaperProps={{ sx: { borderRadius: 3, width: "100%", maxWidth: 460 } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "1rem",
              fontWeight: 800,
              pb: 1,
            }}
          >
            <WarningIcon sx={{ color: WARN }} />
            The amount does not cover the dues
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            {shortfallPrompt && (
              <>
                <Typography
                  sx={{ fontSize: "0.85rem", color: INK_SOFT, mb: 1.5 }}
                >
                  {shortfallPrompt.editing
                    ? `Dated ${dayjs(shortfallPrompt.receiptDate).format(
                        "DD MMM YYYY",
                      )}, this receipt owes ₹${(
                        shortfallPrompt.interestDue +
                        shortfallPrompt.insuranceDue
                      ).toLocaleString(
                        "en-IN",
                      )} — but only ₹${shortfallPrompt.total.toLocaleString(
                        "en-IN",
                      )} is being collected. Changing the date changes the dues; the money on the receipt did not change with it.`
                    : `The dues for ${dayjs(shortfallPrompt.receiptDate).format(
                        "DD MMM YYYY",
                      )} are ₹${(
                        shortfallPrompt.interestDue +
                        shortfallPrompt.insuranceDue
                      ).toLocaleString(
                        "en-IN",
                      )}, but only ₹${shortfallPrompt.total.toLocaleString(
                        "en-IN",
                      )} is being collected.`}
                </Typography>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: "10px",
                    bgcolor: BG_ALT,
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  {[
                    ["Interest due", shortfallPrompt.interestDue],
                    ["Insurance due", shortfallPrompt.insuranceDue],
                    [
                      "Being paid towards interest",
                      shortfallPrompt.interestPaid,
                    ],
                    [
                      "Being paid towards insurance",
                      shortfallPrompt.insurancePaid,
                    ],
                    ...(shortfallPrompt.principalPaid > 0
                      ? [
                          [
                            "Being paid towards principal",
                            shortfallPrompt.principalPaid,
                          ],
                        ]
                      : []),
                  ].map(([label, val]) => (
                    <Box
                      key={label}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.25,
                      }}
                    >
                      <Typography sx={{ fontSize: "0.8rem", color: INK_MUTED }}>
                        {label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          fontFamily: "monospace",
                          color: INK_SOFT,
                        }}
                      >
                        ₹{Number(val).toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 0.75, borderColor: BORDER_MED }} />
                  {shortfallPrompt.shortfallInterest > 0 && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          color: "#92400E",
                        }}
                      >
                        Interest left unpaid
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 800,
                          fontFamily: "monospace",
                          color: "#92400E",
                        }}
                      >
                        ₹
                        {shortfallPrompt.shortfallInterest.toLocaleString(
                          "en-IN",
                        )}
                      </Typography>
                    </Box>
                  )}
                  {shortfallPrompt.shortfallInsurance > 0 && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          color: "#92400E",
                        }}
                      >
                        Insurance left unpaid
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 800,
                          fontFamily: "monospace",
                          color: "#92400E",
                        }}
                      >
                        ₹
                        {shortfallPrompt.shortfallInsurance.toLocaleString(
                          "en-IN",
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography
                  sx={{ fontSize: "0.75rem", color: INK_MUTED, mt: 1.25 }}
                >
                  Money is applied to interest first, then insurance. Anything
                  unpaid is not waived — it carries to the customer's next
                  receipt.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 0.5 }}>
            <Button
              onClick={() => setShortfallPrompt(null)}
              sx={{ textTransform: "none", fontWeight: 700, color: INK_MUTED }}
            >
              Go back and change the amount
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                const ack = shortfallPrompt;
                setShortfallPrompt(null);
                if (ack) handleSaveReceipt(ack.otpVerified, true);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "9px",
                bgcolor: WARN,
                "&:hover": { bgcolor: "#B45309" },
              }}
            >
              Save anyway
            </Button>
          </DialogActions>
        </Dialog>

        {/* Receipt picker — shown when a loan has more than one receipt and the
            operator hits Print. Choosing one opens its pawn ticket. */}
        <Dialog
          open={printDialogOpen}
          onClose={() => setPrintDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: 3, width: "100%", maxWidth: 420 } }}
        >
          <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700, pb: 1 }}>
            Select a receipt to print
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: INK_MUTED, mb: 1, display: "block" }}
            >
              This loan has {pastReceipts.length} receipts. Choose which one to
              print the pawn ticket for.
            </Typography>
            <List sx={{ pt: 0 }}>
              {receiptsNewestFirst.map((rec, idx) => (
                <ListItem key={rec.id ?? idx} disableGutters sx={{ mb: 0.5 }}>
                  <Box
                    onClick={() => printReceipt(rec)}
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      p: 1.25,
                      borderRadius: "10px",
                      border: `1px solid ${BORDER}`,
                      cursor: "pointer",
                      "&:hover": { borderColor: ACCENT, bgcolor: ACCENT_PALE },
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: INK, fontFamily: "monospace" }}
                      >
                        #{rec.receipt_no}
                      </Typography>
                      <Typography variant="caption" sx={{ color: INK_MUTED }}>
                        {formatReceiptWhen(rec)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: ACCENT }}
                      >
                        ₹{Number(rec.amount || 0).toLocaleString("en-IN")}
                      </Typography>
                      <Chip
                        size="small"
                        icon={<PrintIcon sx={{ fontSize: 14 }} />}
                        label="Print"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          bgcolor: ACCENT_LIGHT,
                          color: ACCENT_DARK,
                        }}
                      />
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setPrintDialogOpen(false)} color="inherit">
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Settlement OTP authorisation — reuses the customer-creation OTP
            dialog. Verifying (real OTP or the 123456 test bypass) runs the
            actual settlement save via handleSaveReceipt(true). */}
        <OTPDialog
          open={settlementOtpOpen}
          mobile={formData.mobile}
          storeId={storeId}
          purpose="settlement"
          onClose={() => setSettlementOtpOpen(false)}
          onVerifySuccess={() => {
            setSettlementOtpOpen(false);
            handleSaveReceipt(true);
          }}
        />
      </Box>
    </ThemeProvider>
  );
};

export default PawnReceiptScreen;
